import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from '../../components/PrimeCalendar';
import { TreeTable } from 'primereact/treetable';
import { Chart } from 'primereact/chart';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { useReactToPrint } from 'react-to-print';
import { getRequest, postRequest } from '../../api/ApiController';
import { LocaleContext } from '../../components/LocaleContext';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { DatabaseContext } from '../../redux/DatabaseStore';
import {
  buildEvaluationPrintContentAsync,
  CRM_EVALUATION_PRINT_KEYS,
  evaluationPrintContentHasCharts,
} from '../../utils/evaluationPrintUtils';
import { getEvaluationPrintPageStyle } from '../../utils/evaluationPrintPageStyle';
import { getCurrency } from '../../utils/moduleSettings';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatPercent } from '../../utils/formatters';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import { resolvePrintCompanyLogoSrc } from '../../utils/companyBranding';

function toQuery(params) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(`${key}[]`, String(item)));
      return;
    }
    searchParams.append(key, String(value));
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

const SearchFilterInput = ({ value, onChange }) => (
  <span className="relative w-full">
    <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: '0.9rem' }} />
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="p-inputtext p-component w-full h-[42px] pl-9"
    />
  </span>
);

const fmtMonth = (d) => (d instanceof Date && !Number.isNaN(d.getTime())
  ? `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  : '');

const fmtMonthRange = (rangeVal) => {
  if (!Array.isArray(rangeVal) || !(rangeVal[0] instanceof Date)) return '';
  const s = rangeVal[0];
  const e = rangeVal[1] instanceof Date ? rangeVal[1] : s;
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) return fmtMonth(s);
  return `${fmtMonth(s)} - ${fmtMonth(e)}`;
};
const monthDate = (y, m) => new Date(y, m, 1);
const addMonths = (d, n) => monthDate(d.getFullYear(), d.getMonth() + n);
const getDefaultRightRange = (now = new Date()) => {
  const prev = addMonths(now, -2);
  return [prev, prev];
};
const getDefaultLeftRange = (now = new Date()) => {
  const prev = addMonths(now, -1);
  return [prev, prev];
};
const getDefaultTrendRangeMonth = () => getDefaultLeftRange();

const normalizeRangeBounds = (range, mode = 'month') => {
  const hasStart = Array.isArray(range) && range[0] instanceof Date && !Number.isNaN(range[0].getTime());
  if (!hasStart) return { start: null, end: null };
  const rawStart = range[0];
  const rawEnd = range[1] instanceof Date && !Number.isNaN(range[1].getTime()) ? range[1] : rawStart;
  if (mode === 'year') {
    const startYear = Math.min(rawStart.getFullYear(), rawEnd.getFullYear());
    const endYear = Math.max(rawStart.getFullYear(), rawEnd.getFullYear());
    return {
      start: new Date(startYear, 0, 1),
      end: new Date(endYear, 11, 31, 23, 59, 59, 999),
    };
  }
  const startMonth = new Date(rawStart.getFullYear(), rawStart.getMonth(), 1);
  const endMonth = new Date(rawEnd.getFullYear(), rawEnd.getMonth() + 1, 0, 23, 59, 59, 999);
  return startMonth <= endMonth
    ? { start: startMonth, end: endMonth }
    : {
      start: new Date(rawEnd.getFullYear(), rawEnd.getMonth(), 1),
      end: new Date(rawStart.getFullYear(), rawStart.getMonth() + 1, 0, 23, 59, 59, 999),
    };
};

const periodKeyFromDate = (date, mode = 'month') => (mode === 'year'
  ? `${date.getFullYear()}`
  : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);

const statDate = (stat) => new Date(Number(stat.year || 0), Number(stat.month || 1) - 1, 1);

const truncateLabel = (value, max = 24) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
};


const TREND_MAX_CHARTS = 10;
const TREND_DEFAULT_FIRST_SERIES = ['costPct', 'ordersPct', 'partsPct'];
const DEFAULT_TREND_CHART = { id: 1, chartType: 'bar', displayType: 'pct', series: TREND_DEFAULT_FIRST_SERIES };

const ACK_STATE_RAW = [
  { labelKey: 'CrmEval.Ack.Recognized', value: 'Uznaná' },
  { labelKey: 'CrmEval.Ack.Unrecognized', value: 'Neuznaná' },
];

const DEFAULT_CLAIMED_ORDERS_SORT = [{ field: 'ordersCount', order: -1 }];
const DEFAULT_CLAIMED_PIECES_SORT = [{ field: 'partsCount', order: -1 }];
const DEFAULT_REPAIR_COST_SORT = [{ field: 'costEUR', order: -1 }];
const DEFAULT_TREND_SORT_ABS = [{ field: 'claimsCost', order: -1 }];
const DEFAULT_TREND_SORT_PCT = [{ field: 'costPct', order: -1 }];

const createClaimedOrdersFilters = () => ({
  customer: { value: null, matchMode: 'equals' },
  ordersTotal: { value: null, matchMode: 'contains' },
  ordersCount: { value: null, matchMode: 'contains' },
  ordersPct: { value: null, matchMode: 'contains' },
});

const createClaimedPiecesFilters = () => ({
  customer: { value: null, matchMode: 'equals' },
  partsTotal: { value: null, matchMode: 'contains' },
  partsCount: { value: null, matchMode: 'contains' },
  partsPct: { value: null, matchMode: 'contains' },
});

const createRepairCostFilters = () => ({
  customer: { value: null, matchMode: 'equals' },
  revenue: { value: null, matchMode: 'contains' },
  costEUR: { value: null, matchMode: 'contains' },
  costPct: { value: null, matchMode: 'contains' },
});
const createTrendFilters = () => ({
  departmentName: { value: null, matchMode: 'equals' },
  ordersTotal: { value: null, matchMode: 'contains' },
  ordersCount: { value: null, matchMode: 'contains' },
  ordersPct: { value: null, matchMode: 'contains' },
  partsTotal: { value: null, matchMode: 'contains' },
  partsCount: { value: null, matchMode: 'contains' },
  partsPct: { value: null, matchMode: 'contains' },
  revenue: { value: null, matchMode: 'contains' },
  claimsCost: { value: null, matchMode: 'contains' },
  costPct: { value: null, matchMode: 'contains' },
});

const parseStoredRange = (value) => {
  if (!Array.isArray(value)) return null;
  const [l, r] = value;
  const left = l ? new Date(l) : null;
  const right = r ? new Date(r) : null;
  return left ? [left, right || left] : null;
};

const serializeRange = (value) => (Array.isArray(value)
  ? [value[0]?.toISOString?.() || null, value[1]?.toISOString?.() || null]
  : null);

const sameStringArray = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => String(value) === String(right[index]));
};

function userCanSelectTrader(user) {
  if (!user) return false;
  if (user.is_superuser) return true;
  if (user.permissions?.includes('access settings')) return true;
  if (user.permissions?.includes('access customer_management-complete-list')) return true;
  const roles = user.roles || [];
  return roles.some((role) => {
    const name = typeof role === 'string' ? role : role?.name;
    return name === 'admin' || name === 'Riaditeľ';
  });
}

export default function CustomerEvaluationList({
  endpointPrefix = '/api/customer_evaluations',
  claimEndpointPrefix = null,
}) {
  const claimApiPrefix = claimEndpointPrefix ?? endpointPrefix;
  const CRM_MASK_STORAGE_KEY = 'crm_customer_evaluation_mask_v1';
  const CRM_MASK_PAGE_KEY = 'CustomerEvaluationList';
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const ACK_STATE_OPTIONS = useMemo(
    () => ACK_STATE_RAW.map((o) => ({ label: t(o.labelKey), value: o.value })),
    [t]
  );
  const { toast } = useContext(UtilContext);
  const currencySymbol = getCurrency(state?.module_settings || []);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [printChartFontSize, setPrintChartFontSize] = useState(null);
  const [printDialogVisible, setPrintDialogVisible] = useState(false);
  const [printSelection, setPrintSelection] = useState(['all']);
  const refAllTablesTab2 = useRef(null);
  const refClaimedOrders = useRef(null);
  const refClaimedPieces = useRef(null);
  const refRepairCost = useRef(null);
  const refTrendPeriod = useRef(null);
  const printContainerRef = useRef(null);
  const printSections = [
    { key: 'all', label: t('Common.All') },
    { key: 'claimedOrders', label: t('Menu.CRM.EvaluationCustomers') },
    { key: 'claimedPieces', label: t('CrmEval.Section.ClaimedPieces') },
    { key: 'repairCost', label: t('CrmEval.Section.RepairCost') },
    { key: 'trendPeriod', label: t('CrmEval.Section.Trend') },
  ];

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [traders, setTraders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [claimItems, setClaimItems] = useState([]);
  const [claimStatistics, setClaimStatistics] = useState([]);
  const canSelectTrader = useMemo(() => userCanSelectTrader(user), [user]);
  // logo_print_url → logo_url → product fallback (never logo_url before logo_print_url)
  const crmPrintLogoSrc = useMemo(
    () => resolvePrintCompanyLogoSrc(state?.branding, defaultProductLogoSrc),
    [state?.branding],
  );
  const tableScrollProps = useMemo(() => (
    isPrintMode
      ? {}
      : { scrollable: true, scrollHeight: '500px', scrollDirection: 'both' }
  ), [isPrintMode]);
  const [trendRangeUI, setTrendRangeUI] = useState(() => getDefaultTrendRangeMonth());
  const [trendPeriodMode, setTrendPeriodMode] = useState('month');
  const [trendTableMode, setTrendTableMode] = useState('abs');
  const [expandedTrend, setExpandedTrend] = useState({});
  const [showTrendCharts, setShowTrendCharts] = useState(true);
  const [trendCharts, setTrendCharts] = useState([DEFAULT_TREND_CHART]);
  const [compareClaimedOrdersMode, setCompareClaimedOrdersMode] = useState(false);
  const [compareClaimedPiecesMode, setCompareClaimedPiecesMode] = useState(false);
  const [compareRepairCostMode, setCompareRepairCostMode] = useState(false);
  const [showClaimedOrdersCharts, setShowClaimedOrdersCharts] = useState(false);
  const [showClaimedPiecesCharts, setShowClaimedPiecesCharts] = useState(false);
  const [showRepairCostCharts, setShowRepairCostCharts] = useState(false);
  const [claimedOrdersLeftRange, setClaimedOrdersLeftRange] = useState(() => getDefaultLeftRange());
  const [claimedOrdersRightRange, setClaimedOrdersRightRange] = useState(null);
  const [claimedPiecesLeftRange, setClaimedPiecesLeftRange] = useState(() => getDefaultLeftRange());
  const [claimedPiecesRightRange, setClaimedPiecesRightRange] = useState(null);
  const [repairCostLeftRange, setRepairCostLeftRange] = useState(() => getDefaultLeftRange());
  const [repairCostRightRange, setRepairCostRightRange] = useState(null);
  const [filtersClaimedOrdersLeft, setFiltersClaimedOrdersLeft] = useState(createClaimedOrdersFilters);
  const [filtersClaimedOrdersRight, setFiltersClaimedOrdersRight] = useState(createClaimedOrdersFilters);
  const [filtersClaimedPiecesLeft, setFiltersClaimedPiecesLeft] = useState(createClaimedPiecesFilters);
  const [filtersClaimedPiecesRight, setFiltersClaimedPiecesRight] = useState(createClaimedPiecesFilters);
  const [filtersRepairCostLeft, setFiltersRepairCostLeft] = useState(createRepairCostFilters);
  const [filtersRepairCostRight, setFiltersRepairCostRight] = useState(createRepairCostFilters);
  const [claimedOrdersLeftSortMeta, setClaimedOrdersLeftSortMeta] = useState(DEFAULT_CLAIMED_ORDERS_SORT);
  const [claimedOrdersRightSortMeta, setClaimedOrdersRightSortMeta] = useState(DEFAULT_CLAIMED_ORDERS_SORT);
  const [claimedPiecesLeftSortMeta, setClaimedPiecesLeftSortMeta] = useState(DEFAULT_CLAIMED_PIECES_SORT);
  const [claimedPiecesRightSortMeta, setClaimedPiecesRightSortMeta] = useState(DEFAULT_CLAIMED_PIECES_SORT);
  const [repairCostLeftSortMeta, setRepairCostLeftSortMeta] = useState(DEFAULT_REPAIR_COST_SORT);
  const [repairCostRightSortMeta, setRepairCostRightSortMeta] = useState(DEFAULT_REPAIR_COST_SORT);
  const [filtersTrend, setFiltersTrend] = useState(createTrendFilters);
  const [trendSortMetaAbs, setTrendSortMetaAbs] = useState(DEFAULT_TREND_SORT_ABS);
  const [trendSortMetaPct, setTrendSortMetaPct] = useState(DEFAULT_TREND_SORT_PCT);

  const [selectedAckStates, setSelectedAckStates] = useState([t('CrmEval.Ack.Recognized')]);
  const [customersTouched, setCustomersTouched] = useState(false);
  const [tradersTouched, setTradersTouched] = useState(false);
  const [filters, setFilters] = useState({
    trader_ids: [],
    customer_ids: [],
  });
  const effectiveFilters = useMemo(() => {
    const resolved = { ...filters };
    if (!canSelectTrader && user?.id) {
      resolved.trader_ids = [String(user.id)];
    }
    return resolved;
  }, [filters, canSelectTrader, user?.id]);

  const traderOptions = useMemo(
    () => (traders || []).map((x) => ({ label: x.name, value: String(x.id) })),
    [traders]
  );

  const customerOptions = useMemo(
    () => (customers || []).map((x) => ({ label: x.name, value: String(x.id) })),
    [customers]
  );

  const allCustomerIds = useMemo(
    () => customerOptions.map((o) => o.value),
    [customerOptions]
  );

  const allTraderIds = useMemo(
    () => traderOptions.map((o) => o.value),
    [traderOptions]
  );

  const traderIdsForLoadKey = useMemo(() => {
    if (!canSelectTrader && user?.id) return String(user.id);
    const ids = filters.trader_ids || [];
    if (ids.length === 0) return tradersTouched ? '-1' : '';
    return ids.map(String).join(',');
  }, [canSelectTrader, user?.id, tradersTouched, filters.trader_ids]);

  useEffect(() => {
    setFilters((prev) => {
      const current = prev.customer_ids || [];
      const next = customersTouched
        ? current.filter((id) => allCustomerIds.includes(String(id)))
        : allCustomerIds;
      if (sameStringArray(current, next)) return prev;
      return { ...prev, customer_ids: next };
    });
  }, [allCustomerIds, customersTouched]);

  useEffect(() => {
    if (!canSelectTrader) return;
    if (tradersTouched) return;
    setFilters((prev) => {
      const current = prev.trader_ids || [];
      if (sameStringArray(current, allTraderIds)) return prev;
      return { ...prev, trader_ids: allTraderIds };
    });
  }, [canSelectTrader, allTraderIds, tradersTouched]);

  const scopedClaimItems = useMemo(() => {
    if (customersTouched && filters.customer_ids.length === 0) return [];

    const effectiveCustomerIds = (!customersTouched && filters.customer_ids.length === 0 && allCustomerIds.length > 0)
      ? allCustomerIds
      : (effectiveFilters.customer_ids || []);
    const customerSet = new Set(effectiveCustomerIds.map(String));
    const stateSet = new Set(selectedAckStates || []);

    return (claimItems || []).filter((item) => {
      if (customerSet.size > 0 && !customerSet.has(String(item.customer_id))) return false;
      if (stateSet.size > 0 && !stateSet.has(item.ack_error)) return false;
      return true;
    });
  }, [
    claimItems,
    customersTouched,
    filters.customer_ids.length,
    allCustomerIds,
    effectiveFilters.customer_ids,
    selectedAckStates,
  ]);

  const scopedClaimStatistics = useMemo(() => {
    if (customersTouched && filters.customer_ids.length === 0) return [];

    const effectiveCustomerIds = (!customersTouched && filters.customer_ids.length === 0 && allCustomerIds.length > 0)
      ? allCustomerIds
      : (effectiveFilters.customer_ids || []);
    const customerSet = new Set(effectiveCustomerIds.map(String));

    return (claimStatistics || []).filter((item) => {
      if (customerSet.size > 0 && !customerSet.has(String(item.company_id))) return false;
      return true;
    });
  }, [
    claimStatistics,
    customersTouched,
    filters.customer_ids.length,
    allCustomerIds,
    effectiveFilters.customer_ids,
  ]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const traderIdsForLoad = traderIdsForLoadKey
        ? traderIdsForLoadKey.split(',').filter(Boolean)
        : [];
      const claimResponse = await getRequest(`${claimApiPrefix}/claim_evaluation${toQuery({
        trader_ids: traderIdsForLoad,
      })}`);

      const claimData = claimResponse?.data ?? claimResponse ?? {};
      setTraders(Array.isArray(claimData.traders) ? claimData.traders : []);
      setCustomers(Array.isArray(claimData.customers) ? claimData.customers : []);
      setClaimItems(Array.isArray(claimData.items) ? claimData.items : []);
      setClaimStatistics(Array.isArray(claimData.statistics) ? claimData.statistics : []);
    } catch (error) {
      setLoadError(error?.message || t('CustomerEvaluationList.Toast.LoadErrorDetail'));
      setClaimItems([]);
      setClaimStatistics([]);
      toast?.current?.show({
        severity: 'error',
        summary: t('CustomerEvaluationList.Toast.LoadErrorTitle'),
        detail: error?.message || t('CustomerEvaluationList.Toast.LoadErrorDetail'),
        life: 3500,
      });
    } finally {
      setLoading(false);
    }
  }, [traderIdsForLoadKey, claimApiPrefix, t, toast]);

  useEffect(() => {
    if (!user?.id) return;
    loadData();
  }, [loadData, user?.id]);

  useEffect(() => {
    if (compareClaimedOrdersMode && !claimedOrdersRightRange) {
      setClaimedOrdersRightRange(getDefaultRightRange());
    }
  }, [compareClaimedOrdersMode, claimedOrdersRightRange]);

  useEffect(() => {
    if (compareClaimedPiecesMode && !claimedPiecesRightRange) {
      setClaimedPiecesRightRange(getDefaultRightRange());
    }
  }, [compareClaimedPiecesMode, claimedPiecesRightRange]);

  useEffect(() => {
    if (compareRepairCostMode && !repairCostRightRange) {
      setRepairCostRightRange(getDefaultRightRange());
    }
  }, [compareRepairCostMode, repairCostRightRange]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const applyMaskPayload = useCallback((parsed) => {
    if (!parsed) return;
    try {
      if (parsed?.filters) {
        setFilters((prev) => ({
          ...prev,
          trader_ids: Array.isArray(parsed.filters.trader_ids) ? parsed.filters.trader_ids : prev.trader_ids,
          customer_ids: Array.isArray(parsed.filters.customer_ids) ? parsed.filters.customer_ids : prev.customer_ids,
        }));
      }
      if (Array.isArray(parsed?.selectedAckStates)) {
        setSelectedAckStates(parsed.selectedAckStates);
      }
      if (typeof parsed?.customersTouched === 'boolean') setCustomersTouched(parsed.customersTouched);
      if (typeof parsed?.tradersTouched === 'boolean') setTradersTouched(parsed.tradersTouched);
      if (Array.isArray(parsed?.trendRangeUI)) {
        setTrendRangeUI(parseStoredRange(parsed.trendRangeUI));
      }
      if (parsed?.trendPeriodMode === 'month' || parsed?.trendPeriodMode === 'year') {
        setTrendPeriodMode(parsed.trendPeriodMode);
      }
      if (parsed?.trendTableMode === 'abs' || parsed?.trendTableMode === 'pct') {
        setTrendTableMode(parsed.trendTableMode);
      }
      if (typeof parsed?.compareClaimedOrdersMode === 'boolean') {
        setCompareClaimedOrdersMode(parsed.compareClaimedOrdersMode);
      }
      if (typeof parsed?.compareClaimedPiecesMode === 'boolean') setCompareClaimedPiecesMode(parsed.compareClaimedPiecesMode);
      if (typeof parsed?.compareRepairCostMode === 'boolean') setCompareRepairCostMode(parsed.compareRepairCostMode);
      if (typeof parsed?.showClaimedOrdersCharts === 'boolean') setShowClaimedOrdersCharts(parsed.showClaimedOrdersCharts);
      if (typeof parsed?.showClaimedPiecesCharts === 'boolean') setShowClaimedPiecesCharts(parsed.showClaimedPiecesCharts);
      if (typeof parsed?.showRepairCostCharts === 'boolean') setShowRepairCostCharts(parsed.showRepairCostCharts);
      if (typeof parsed?.showTrendCharts === 'boolean') setShowTrendCharts(parsed.showTrendCharts);
      if (Array.isArray(parsed?.claimedOrdersLeftRange)) setClaimedOrdersLeftRange(parseStoredRange(parsed.claimedOrdersLeftRange));
      if (Array.isArray(parsed?.claimedOrdersRightRange)) setClaimedOrdersRightRange(parseStoredRange(parsed.claimedOrdersRightRange));
      if (Array.isArray(parsed?.claimedPiecesLeftRange)) setClaimedPiecesLeftRange(parseStoredRange(parsed.claimedPiecesLeftRange));
      if (Array.isArray(parsed?.claimedPiecesRightRange)) setClaimedPiecesRightRange(parseStoredRange(parsed.claimedPiecesRightRange));
      if (Array.isArray(parsed?.repairCostLeftRange)) setRepairCostLeftRange(parseStoredRange(parsed.repairCostLeftRange));
      if (Array.isArray(parsed?.repairCostRightRange)) setRepairCostRightRange(parseStoredRange(parsed.repairCostRightRange));
      if (Array.isArray(parsed?.trendCharts) && parsed.trendCharts.length > 0) {
        const mapLegacySeries = (series = []) => {
          const mapped = (series || []).map((s) => {
            if (s === 'orders') return 'ordersCount';
            if (s === 'parts') return 'partsCount';
            if (s === 'cost') return 'claimsCost';
            return s;
          });
          return mapped.length > 0 ? mapped : ['ordersPct', 'partsPct', 'costPct'];
        };
        setTrendCharts(parsed.trendCharts.slice(0, TREND_MAX_CHARTS).map((c, idx) => ({
          id: c?.id || Date.now() + idx,
          chartType: c?.chartType === 'bar' ? 'bar' : 'line',
          displayType: c?.displayType || 'pct',
          series: mapLegacySeries(Array.isArray(c?.series) ? c.series : []),
        })));
      }
      if (parsed?.tableFilters) {
        if (parsed.tableFilters.claimedOrdersLeft) setFiltersClaimedOrdersLeft(parsed.tableFilters.claimedOrdersLeft);
        if (parsed.tableFilters.claimedOrdersRight) setFiltersClaimedOrdersRight(parsed.tableFilters.claimedOrdersRight);
        if (parsed.tableFilters.claimedPiecesLeft) setFiltersClaimedPiecesLeft(parsed.tableFilters.claimedPiecesLeft);
        if (parsed.tableFilters.claimedPiecesRight) setFiltersClaimedPiecesRight(parsed.tableFilters.claimedPiecesRight);
        if (parsed.tableFilters.repairCostLeft) setFiltersRepairCostLeft(parsed.tableFilters.repairCostLeft);
        if (parsed.tableFilters.repairCostRight) setFiltersRepairCostRight(parsed.tableFilters.repairCostRight);
        if (parsed.tableFilters.trend) setFiltersTrend(parsed.tableFilters.trend);
      }
      if (parsed?.tableSorts) {
        if (Array.isArray(parsed.tableSorts.claimedOrdersLeft)) setClaimedOrdersLeftSortMeta(parsed.tableSorts.claimedOrdersLeft);
        if (Array.isArray(parsed.tableSorts.claimedOrdersRight)) setClaimedOrdersRightSortMeta(parsed.tableSorts.claimedOrdersRight);
        if (Array.isArray(parsed.tableSorts.claimedPiecesLeft)) setClaimedPiecesLeftSortMeta(parsed.tableSorts.claimedPiecesLeft);
        if (Array.isArray(parsed.tableSorts.claimedPiecesRight)) setClaimedPiecesRightSortMeta(parsed.tableSorts.claimedPiecesRight);
        if (Array.isArray(parsed.tableSorts.repairCostLeft)) setRepairCostLeftSortMeta(parsed.tableSorts.repairCostLeft);
        if (Array.isArray(parsed.tableSorts.repairCostRight)) setRepairCostRightSortMeta(parsed.tableSorts.repairCostRight);
        if (Array.isArray(parsed.tableSorts.trendAbs)) setTrendSortMetaAbs(parsed.tableSorts.trendAbs);
        if (Array.isArray(parsed.tableSorts.trendPct)) setTrendSortMetaPct(parsed.tableSorts.trendPct);
      }
    } catch (_) {
      // ignore broken persisted mask
    }
  }, []);

  useEffect(() => {
    const row = (state?.user_mask_filters || []).find((m) => m.page === CRM_MASK_PAGE_KEY);
    if (row?.attributes) {
      try {
        const parsed = typeof row.attributes === 'string' ? JSON.parse(row.attributes) : row.attributes;
        applyMaskPayload(parsed);
        return;
      } catch (_) {
        // fallback to local storage
      }
    }
    try {
      const raw = window.localStorage.getItem(CRM_MASK_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      applyMaskPayload(parsed);
    } catch (_) {
      // ignore broken persisted mask
    }
  }, [state?.user_mask_filters, applyMaskPayload]);

  const saveMask = useCallback(async () => {
    try {
      const payload = {
        filters: {
          trader_ids: effectiveFilters.trader_ids || [],
          customer_ids: effectiveFilters.customer_ids || [],
        },
        selectedAckStates,
        customersTouched,
        tradersTouched,
        trendRangeUI: Array.isArray(trendRangeUI)
          ? serializeRange(trendRangeUI)
          : null,
        trendPeriodMode,
        trendTableMode,
        compareClaimedOrdersMode,
        compareClaimedPiecesMode,
        compareRepairCostMode,
        showClaimedOrdersCharts,
        showClaimedPiecesCharts,
        showRepairCostCharts,
        showTrendCharts,
        claimedOrdersLeftRange: serializeRange(claimedOrdersLeftRange),
        claimedOrdersRightRange: serializeRange(claimedOrdersRightRange),
        claimedPiecesLeftRange: serializeRange(claimedPiecesLeftRange),
        claimedPiecesRightRange: serializeRange(claimedPiecesRightRange),
        repairCostLeftRange: serializeRange(repairCostLeftRange),
        repairCostRightRange: serializeRange(repairCostRightRange),
        trendCharts: (trendCharts || []).slice(0, TREND_MAX_CHARTS),
        tableFilters: {
          claimedOrdersLeft: filtersClaimedOrdersLeft,
          claimedOrdersRight: filtersClaimedOrdersRight,
          claimedPiecesLeft: filtersClaimedPiecesLeft,
          claimedPiecesRight: filtersClaimedPiecesRight,
          repairCostLeft: filtersRepairCostLeft,
          repairCostRight: filtersRepairCostRight,
          trend: filtersTrend,
        },
        tableSorts: {
          claimedOrdersLeft: claimedOrdersLeftSortMeta,
          claimedOrdersRight: claimedOrdersRightSortMeta,
          claimedPiecesLeft: claimedPiecesLeftSortMeta,
          claimedPiecesRight: claimedPiecesRightSortMeta,
          repairCostLeft: repairCostLeftSortMeta,
          repairCostRight: repairCostRightSortMeta,
          trendAbs: trendSortMetaAbs,
          trendPct: trendSortMetaPct,
        },
      };

      const fd = new FormData();
      fd.append('id', '');
      fd.append('page', CRM_MASK_PAGE_KEY);
      fd.append('attributes', JSON.stringify(payload));
      fd.append('filters', JSON.stringify([]));
      fd.append('sorters', JSON.stringify([]));
      fd.append('columns', JSON.stringify({}));

      const response = await postRequest('/api/user_mask_filters', fd, true);
      if (response?.success && Array.isArray(response?.data)) {
        dispatch({ type: 'load_user_mask_filters', newUserMaskFilters: response.data });
      }
      window.localStorage.setItem(CRM_MASK_STORAGE_KEY, JSON.stringify(payload));
      toast?.current?.show({
        severity: 'success',
        summary: t('Common.Mask'),
        detail: t('Common.Toast.MaskSavedDetail'),
        life: 2200,
      });
    } catch (_) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Mask'),
        detail: t('Common.Toast.MaskSaveFailedDetail'),
        life: 2600,
      });
    }
  }, [
    dispatch,
    CRM_MASK_PAGE_KEY,
    effectiveFilters.trader_ids,
    effectiveFilters.customer_ids,
    selectedAckStates,
    customersTouched,
    tradersTouched,
    trendRangeUI,
    trendPeriodMode,
    trendTableMode,
    compareClaimedOrdersMode,
    compareClaimedPiecesMode,
    compareRepairCostMode,
    showClaimedOrdersCharts,
    showClaimedPiecesCharts,
    showRepairCostCharts,
    showTrendCharts,
    claimedOrdersLeftRange,
    claimedOrdersRightRange,
    claimedPiecesLeftRange,
    claimedPiecesRightRange,
    repairCostLeftRange,
    repairCostRightRange,
    trendCharts,
    filtersClaimedOrdersLeft,
    filtersClaimedOrdersRight,
    filtersClaimedPiecesLeft,
    filtersClaimedPiecesRight,
    filtersRepairCostLeft,
    filtersRepairCostRight,
    filtersTrend,
    claimedOrdersLeftSortMeta,
    claimedOrdersRightSortMeta,
    claimedPiecesLeftSortMeta,
    claimedPiecesRightSortMeta,
    repairCostLeftSortMeta,
    repairCostRightSortMeta,
    trendSortMetaAbs,
    trendSortMetaPct,
    toast,
  ]);

  const resetMask = useCallback(() => {
    setFilters({
      trader_ids: [],
      customer_ids: [],
    });
    setSelectedAckStates([t('CrmEval.Ack.Recognized')]);
    setCustomersTouched(false);
    setTradersTouched(false);
    setTrendRangeUI(getDefaultTrendRangeMonth());
    setTrendPeriodMode('month');
    setTrendTableMode('abs');
    setCompareClaimedOrdersMode(false);
    setCompareClaimedPiecesMode(false);
    setCompareRepairCostMode(false);
    setShowClaimedOrdersCharts(false);
    setShowClaimedPiecesCharts(false);
    setShowRepairCostCharts(false);
    setShowTrendCharts(true);
    setClaimedOrdersLeftRange(getDefaultLeftRange());
    setClaimedOrdersRightRange(null);
    setClaimedPiecesLeftRange(getDefaultLeftRange());
    setClaimedPiecesRightRange(null);
    setRepairCostLeftRange(getDefaultLeftRange());
    setRepairCostRightRange(null);
    setTrendCharts([DEFAULT_TREND_CHART]);
    setExpandedTrend({});
    setFiltersClaimedOrdersLeft(createClaimedOrdersFilters());
    setFiltersClaimedOrdersRight(createClaimedOrdersFilters());
    setFiltersClaimedPiecesLeft(createClaimedPiecesFilters());
    setFiltersClaimedPiecesRight(createClaimedPiecesFilters());
    setFiltersRepairCostLeft(createRepairCostFilters());
    setFiltersRepairCostRight(createRepairCostFilters());
    setFiltersTrend(createTrendFilters());
    setClaimedOrdersLeftSortMeta(DEFAULT_CLAIMED_ORDERS_SORT);
    setClaimedOrdersRightSortMeta(DEFAULT_CLAIMED_ORDERS_SORT);
    setClaimedPiecesLeftSortMeta(DEFAULT_CLAIMED_PIECES_SORT);
    setClaimedPiecesRightSortMeta(DEFAULT_CLAIMED_PIECES_SORT);
    setRepairCostLeftSortMeta(DEFAULT_REPAIR_COST_SORT);
    setRepairCostRightSortMeta(DEFAULT_REPAIR_COST_SORT);
    setTrendSortMetaAbs(DEFAULT_TREND_SORT_ABS);
    setTrendSortMetaPct(DEFAULT_TREND_SORT_PCT);
    try {
      window.localStorage.removeItem(CRM_MASK_STORAGE_KEY);
    } catch (_) {
      // ignore storage errors
    }
    dispatch({
      type: 'load_user_mask_filters',
      newUserMaskFilters: (state.user_mask_filters || []).filter((m) => m.page !== CRM_MASK_PAGE_KEY),
    });
    toast?.current?.show({
      severity: 'info',
      summary: t('Common.Mask'),
      detail: t('Common.Toast.MaskResetDetail'),
      life: 2200,
    });
  }, [toast, dispatch, state.user_mask_filters, CRM_MASK_PAGE_KEY]);

  const customerNameById = useMemo(() => {
    const map = new Map();
    (customers || []).forEach((customer) => {
      map.set(String(customer.id), customer.name || `#${customer.id}`);
    });
    (claimItems || []).forEach((item) => {
      if (item?.customer_id && item?.customer_name && !map.has(String(item.customer_id))) {
        map.set(String(item.customer_id), item.customer_name);
      }
    });
    return map;
  }, [customers, claimItems]);

  const filterClaimItemsByRange = useCallback((sourceItems, range, mode = 'month') => {
    const { start, end } = normalizeRangeBounds(range, mode);

    return (sourceItems || []).filter((item) => {
      if (!start || !end) return true;
      const dt = item.created_at ? new Date(item.created_at) : null;
      return dt && !Number.isNaN(dt.getTime()) && dt >= start && dt <= end;
    });
  }, []);

  const filterStatisticsByRange = useCallback((sourceStats, range, mode = 'month') => {
    const { start, end } = normalizeRangeBounds(range, mode);

    return (sourceStats || []).filter((stat) => {
      const dt = statDate(stat);
      if (Number.isNaN(dt.getTime())) return false;
      if (!start || !end) return true;
      return dt >= start && dt <= end;
    });
  }, []);

  const trendRows = useMemo(() => {
    const periodMap = new Map();
    const ensurePeriod = (periodKey) => {
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, {
          key: periodKey,
          data: {
            departmentName: periodKey,
            ordersTotal: 0,
            ordersCount: 0,
            ordersPct: 0,
            partsTotal: 0,
            partsCount: 0,
            partsPct: 0,
            revenue: 0,
            claimsCost: 0,
            costPct: 0,
          },
          orderNumbers: new Set(),
          children: [],
        });
      }

      return periodMap.get(periodKey);
    };

    filterStatisticsByRange(scopedClaimStatistics, trendRangeUI, trendPeriodMode).forEach((stat) => {
      const periodDate = statDate(stat);
      const node = ensurePeriod(periodKeyFromDate(periodDate, trendPeriodMode));
      node.data.ordersTotal += Number(stat.orders_count || 0);
      node.data.partsTotal += Number(stat.items_count || 0);
      node.data.revenue += Number(stat.monthly_revenue || 0);
    });

    filterClaimItemsByRange(scopedClaimItems, trendRangeUI, trendPeriodMode).forEach((item) => {
      if (!item.created_at) return;
      const dt = new Date(item.created_at);
      if (Number.isNaN(dt.getTime())) return;

      const node = ensurePeriod(periodKeyFromDate(dt, trendPeriodMode));
      const orderNo = String(item.order_number || item.id || '');
      if (orderNo) node.orderNumbers.add(orderNo);
      node.data.ordersCount = node.orderNumbers.size;
      node.data.partsCount += Number(item.defective_pieces || 0);
      node.data.claimsCost += Number(item.repair_costs || 0);
    });

    const sorted = [...periodMap.values()].sort((a, b) => a.key.localeCompare(b.key));
    sorted.forEach((node) => {
      node.data.ordersPct = node.data.ordersTotal > 0 ? (node.data.ordersCount / node.data.ordersTotal) * 100 : 0;
      node.data.partsPct = node.data.partsTotal > 0 ? (node.data.partsCount / node.data.partsTotal) * 100 : 0;
      node.data.costPct = node.data.revenue > 0 ? (node.data.claimsCost / node.data.revenue) * 100 : 0;
      delete node.orderNumbers;
    });
    return sorted;
  }, [scopedClaimItems, scopedClaimStatistics, trendRangeUI, trendPeriodMode, filterClaimItemsByRange, filterStatisticsByRange]);
  const trendSortMeta = trendTableMode === 'abs' ? trendSortMetaAbs : trendSortMetaPct;
  const setTrendSortMeta = useCallback((val) => {
    if (trendTableMode === 'abs') setTrendSortMetaAbs(val || []);
    else setTrendSortMetaPct(val || []);
  }, [trendTableMode]);
  const trendCustomerNameOptions = useMemo(() => {
    const base = trendRows.map((r) => r?.data?.departmentName).filter(Boolean).sort((a, b) => a.localeCompare(b))
      .map((x) => ({ label: x, value: x }));
    return [{ label: t('Common.AllMale'), value: null }, ...base];
  }, [trendRows]);
  const containsNum = useCallback((value, query) => {
    if (!query && query !== 0) return true;
    return String(value ?? '').toLowerCase().includes(String(query).toLowerCase().trim());
  }, []);
  const trendFilteredRows = useMemo(() => trendRows.filter((n) => {
    const d = n?.data || {};
    const byName = filtersTrend.departmentName?.value;
    if (byName && String(d.departmentName || '') !== String(byName)) return false;
    if (!containsNum(d.ordersTotal, filtersTrend.ordersTotal?.value)) return false;
    if (!containsNum(d.ordersCount, filtersTrend.ordersCount?.value)) return false;
    if (!containsNum(d.ordersPct, filtersTrend.ordersPct?.value)) return false;
    if (!containsNum(d.partsTotal, filtersTrend.partsTotal?.value)) return false;
    if (!containsNum(d.partsCount, filtersTrend.partsCount?.value)) return false;
    if (!containsNum(d.partsPct, filtersTrend.partsPct?.value)) return false;
    if (!containsNum(d.revenue, filtersTrend.revenue?.value)) return false;
    if (!containsNum(d.claimsCost, filtersTrend.claimsCost?.value)) return false;
    if (!containsNum(d.costPct, filtersTrend.costPct?.value)) return false;
    return true;
  }), [trendRows, filtersTrend, containsNum]);
  const totalsTrend = useMemo(() => {
    const total = trendFilteredRows.reduce((acc, n) => {
      const d = n?.data || {};
      acc.ordersTotal += Number(d.ordersTotal || 0);
      acc.ordersCount += Number(d.ordersCount || 0);
      acc.partsTotal += Number(d.partsTotal || 0);
      acc.partsCount += Number(d.partsCount || 0);
      acc.revenue += Number(d.revenue || 0);
      acc.claimsCost += Number(d.claimsCost || 0);
      return acc;
    }, {
      ordersTotal: 0, ordersCount: 0, partsTotal: 0, partsCount: 0, revenue: 0, claimsCost: 0,
    });
    total.ordersPct = total.ordersTotal > 0 ? (total.ordersCount / total.ordersTotal) * 100 : 0;
    total.partsPct = total.partsTotal > 0 ? (total.partsCount / total.partsTotal) * 100 : 0;
    total.costPct = total.revenue > 0 ? (total.claimsCost / total.revenue) * 100 : 0;
    return total;
  }, [trendFilteredRows]);

  const aggregateCustomerMetrics = useCallback((sourceItems, range) => {
    const map = new Map();
    const ensureRow = (customerId, fallbackName = '') => {
      const key = String(customerId);
      if (!map.has(key)) {
        map.set(key, {
          _key: key,
          customer: customerNameById.get(key) || fallbackName || `#${customerId}`,
          customer_id: Number(customerId),
          ordersTotal: 0,
          ordersCount: 0,
          ordersPct: 0,
          partsTotal: 0,
          partsCount: 0,
          partsPct: 0,
          revenue: 0,
          costEUR: 0,
          costPct: 0,
          claimedOrderNumbers: new Set(),
        });
      }

      return map.get(key);
    };

    filterStatisticsByRange(scopedClaimStatistics, range).forEach((stat) => {
      const row = ensureRow(stat.company_id);
      row.ordersTotal += Number(stat.orders_count || 0);
      row.partsTotal += Number(stat.items_count || 0);
      row.revenue += Number(stat.monthly_revenue || 0);
    });

    filterClaimItemsByRange(sourceItems, range).forEach((item) => {
      const row = ensureRow(item.customer_id, item.customer_name);
      const orderNo = String(item.order_number || item.id || '');
      if (orderNo) row.claimedOrderNumbers.add(orderNo);
      row.ordersCount = row.claimedOrderNumbers.size;
      row.partsCount += Number(item.defective_pieces || 0);
      row.costEUR += Number(item.repair_costs || 0);
    });

    const result = [...map.values()].map((item) => {
      const next = {
        ...item,
        ordersPct: item.ordersTotal > 0 ? (item.ordersCount / item.ordersTotal) * 100 : 0,
        partsPct: item.partsTotal > 0 ? (item.partsCount / item.partsTotal) * 100 : 0,
        costPct: item.revenue > 0 ? (item.costEUR / item.revenue) * 100 : 0,
      };
      delete next.claimedOrderNumbers;
      return next;
    });
    result.sort((a, b) => a.customer.localeCompare(b.customer));
    return result;
  }, [customerNameById, scopedClaimStatistics, filterClaimItemsByRange, filterStatisticsByRange]);

  const aggregateClaimedOrdersByCustomer = useCallback(
    (sourceItems, range) => aggregateCustomerMetrics(sourceItems, range),
    [aggregateCustomerMetrics]
  );

  const aggregateClaimedPiecesByCustomer = useCallback(
    (sourceItems, range) => aggregateCustomerMetrics(sourceItems, range),
    [aggregateCustomerMetrics]
  );

  const aggregateRepairCostsByCustomer = useCallback(
    (sourceItems, range) => aggregateCustomerMetrics(sourceItems, range),
    [aggregateCustomerMetrics]
  );

  const openProductionQualityDrilldown = useCallback((customerId, range) => {
    if (!customerId) return;
    const props = {
      customerIds: [Number(customerId)],
      presetCustomerIds: [Number(customerId)],
    };
    if (Array.isArray(range) && range[0] instanceof Date) {
      const { start, end } = normalizeRangeBounds(range);
      props.timePeriod = t('ServiceTaskPreview.Custom');
      props.date = [start, end];
    }
    dispatch({
      type: 'create_new_tab',
      name: t('Menu.CRM.ProductionQuality'), translationKey: 'Menu.CRM.ProductionQuality', legacyLabel: t('Menu.CRM.ProductionQuality'),
      page: 'CustomerProductionQuality',
      mask: 'cust004',
      props,
    });
  }, [dispatch]);

  const renderClaimDrilldownCell = useCallback((value, row, range) => {
    const count = Number(value || 0);
    if (!count || !row?.customer_id) return fmtInt(count);
    return (
      <button
        type="button"
        className="text-blue-600 hover:underline cursor-pointer bg-transparent border-0 p-0"
        onClick={() => openProductionQualityDrilldown(row.customer_id, range)}
      >
        {fmtInt(count)}
      </button>
    );
  }, [openProductionQualityDrilldown]);

  const filterCustomerMetricRows = useCallback((rows, tableFilters) => (rows || []).filter((row) => {
    const byCustomer = tableFilters.customer?.value;
    if (byCustomer && String(row.customer || '') !== String(byCustomer)) return false;
    if (!containsNum(row.ordersTotal, tableFilters.ordersTotal?.value)) return false;
    if (!containsNum(row.ordersCount, tableFilters.ordersCount?.value)) return false;
    if (!containsNum(row.ordersPct, tableFilters.ordersPct?.value)) return false;
    if (!containsNum(row.partsTotal, tableFilters.partsTotal?.value)) return false;
    if (!containsNum(row.partsCount, tableFilters.partsCount?.value)) return false;
    if (!containsNum(row.partsPct, tableFilters.partsPct?.value)) return false;
    if (!containsNum(row.revenue, tableFilters.revenue?.value)) return false;
    if (!containsNum(row.costEUR, tableFilters.costEUR?.value)) return false;
    if (!containsNum(row.costPct, tableFilters.costPct?.value)) return false;
    return true;
  }), [containsNum]);

  const claimedOrdersLeftRowsRaw = useMemo(
    () => aggregateClaimedOrdersByCustomer(scopedClaimItems, claimedOrdersLeftRange),
    [scopedClaimItems, claimedOrdersLeftRange, aggregateClaimedOrdersByCustomer]
  );

  const claimedOrdersRightRowsRaw = useMemo(
    () => aggregateClaimedOrdersByCustomer(scopedClaimItems, claimedOrdersRightRange),
    [scopedClaimItems, claimedOrdersRightRange, aggregateClaimedOrdersByCustomer]
  );

  const claimedOrdersLeftRowsAll = useMemo(
    () => filterCustomerMetricRows(claimedOrdersLeftRowsRaw, filtersClaimedOrdersLeft),
    [claimedOrdersLeftRowsRaw, filtersClaimedOrdersLeft, filterCustomerMetricRows]
  );

  const claimedOrdersRightRowsAll = useMemo(
    () => filterCustomerMetricRows(claimedOrdersRightRowsRaw, filtersClaimedOrdersRight),
    [claimedOrdersRightRowsRaw, filtersClaimedOrdersRight, filterCustomerMetricRows]
  );

  const totalsClaimedOrdersLeft = useMemo(() => {
    const total = claimedOrdersLeftRowsAll.reduce((acc, r) => {
      acc.ordersTotal += Number(r.ordersTotal || 0);
      acc.ordersCount += Number(r.ordersCount || 0);
      return acc;
    }, { ordersTotal: 0, ordersCount: 0 });
    total.ordersPct = total.ordersTotal > 0 ? (total.ordersCount / total.ordersTotal) * 100 : 0;
    return total;
  }, [claimedOrdersLeftRowsAll]);

  const totalsClaimedOrdersRight = useMemo(() => {
    const total = claimedOrdersRightRowsAll.reduce((acc, r) => {
      acc.ordersTotal += Number(r.ordersTotal || 0);
      acc.ordersCount += Number(r.ordersCount || 0);
      return acc;
    }, { ordersTotal: 0, ordersCount: 0 });
    total.ordersPct = total.ordersTotal > 0 ? (total.ordersCount / total.ordersTotal) * 100 : 0;
    return total;
  }, [claimedOrdersRightRowsAll]);

  const claimedPiecesLeftRowsRaw = useMemo(
    () => aggregateClaimedPiecesByCustomer(scopedClaimItems, claimedPiecesLeftRange),
    [scopedClaimItems, claimedPiecesLeftRange, aggregateClaimedPiecesByCustomer]
  );
  const claimedPiecesRightRowsRaw = useMemo(
    () => aggregateClaimedPiecesByCustomer(scopedClaimItems, claimedPiecesRightRange),
    [scopedClaimItems, claimedPiecesRightRange, aggregateClaimedPiecesByCustomer]
  );

  const claimedPiecesLeftRowsAll = useMemo(
    () => filterCustomerMetricRows(claimedPiecesLeftRowsRaw, filtersClaimedPiecesLeft),
    [claimedPiecesLeftRowsRaw, filtersClaimedPiecesLeft, filterCustomerMetricRows]
  );
  const claimedPiecesRightRowsAll = useMemo(
    () => filterCustomerMetricRows(claimedPiecesRightRowsRaw, filtersClaimedPiecesRight),
    [claimedPiecesRightRowsRaw, filtersClaimedPiecesRight, filterCustomerMetricRows]
  );

  const totalsClaimedPiecesLeft = useMemo(() => {
    const total = claimedPiecesLeftRowsAll.reduce((acc, r) => {
      acc.partsTotal += Number(r.partsTotal || 0);
      acc.partsCount += Number(r.partsCount || 0);
      return acc;
    }, { partsTotal: 0, partsCount: 0 });
    total.partsPct = total.partsTotal > 0 ? (total.partsCount / total.partsTotal) * 100 : 0;
    return total;
  }, [claimedPiecesLeftRowsAll]);

  const totalsClaimedPiecesRight = useMemo(() => {
    const total = claimedPiecesRightRowsAll.reduce((acc, r) => {
      acc.partsTotal += Number(r.partsTotal || 0);
      acc.partsCount += Number(r.partsCount || 0);
      return acc;
    }, { partsTotal: 0, partsCount: 0 });
    total.partsPct = total.partsTotal > 0 ? (total.partsCount / total.partsTotal) * 100 : 0;
    return total;
  }, [claimedPiecesRightRowsAll]);

  const repairCostLeftRowsRaw = useMemo(
    () => aggregateRepairCostsByCustomer(scopedClaimItems, repairCostLeftRange),
    [scopedClaimItems, repairCostLeftRange, aggregateRepairCostsByCustomer]
  );
  const repairCostRightRowsRaw = useMemo(
    () => aggregateRepairCostsByCustomer(scopedClaimItems, repairCostRightRange),
    [scopedClaimItems, repairCostRightRange, aggregateRepairCostsByCustomer]
  );

  const repairCostLeftRowsAll = useMemo(
    () => filterCustomerMetricRows(repairCostLeftRowsRaw, filtersRepairCostLeft),
    [repairCostLeftRowsRaw, filtersRepairCostLeft, filterCustomerMetricRows]
  );
  const repairCostRightRowsAll = useMemo(
    () => filterCustomerMetricRows(repairCostRightRowsRaw, filtersRepairCostRight),
    [repairCostRightRowsRaw, filtersRepairCostRight, filterCustomerMetricRows]
  );

  const totalsRepairCostLeft = useMemo(() => {
    const total = repairCostLeftRowsAll.reduce((acc, r) => {
      acc.revenue += Number(r.revenue || 0);
      acc.costEUR += Number(r.costEUR || 0);
      return acc;
    }, { revenue: 0, costEUR: 0 });
    total.costPct = total.revenue > 0 ? (total.costEUR / total.revenue) * 100 : 0;
    return total;
  }, [repairCostLeftRowsAll]);

  const totalsRepairCostRight = useMemo(() => {
    const total = repairCostRightRowsAll.reduce((acc, r) => {
      acc.revenue += Number(r.revenue || 0);
      acc.costEUR += Number(r.costEUR || 0);
      return acc;
    }, { revenue: 0, costEUR: 0 });
    total.costPct = total.revenue > 0 ? (total.costEUR / total.revenue) * 100 : 0;
    return total;
  }, [repairCostRightRowsAll]);

  const customerOptionsForClaimedOrders = useMemo(() => {
    const names = new Set();
    const all = [...claimedOrdersLeftRowsRaw, ...claimedOrdersRightRowsRaw];
    all.forEach((row) => {
      if (row?.customer) names.add(row.customer);
    });
    return [...names].sort((a, b) => a.localeCompare(b)).map((name) => ({ label: name, value: name }));
  }, [claimedOrdersLeftRowsRaw, claimedOrdersRightRowsRaw]);

  const TREND_METRICS = useMemo(() => ([
    { label: t('CrmEval.Metric.OrdersTotal'), value: 'ordersTotal', kind: 'count', color: '#fca5a5' },
    { label: t('CrmEval.Metric.OrdersCount'), value: 'ordersCount', kind: 'count', color: '#ef4444' },
    { label: t('CrmEval.Metric.OrdersPct'), value: 'ordersPct', kind: 'pct', color: '#ef4444' },
    { label: t('CrmEval.Metric.PartsTotal'), value: 'partsTotal', kind: 'count', color: '#fcd34d' },
    { label: t('CrmEval.Metric.PartsCount'), value: 'partsCount', kind: 'count', color: '#f59e0b' },
    { label: t('CrmEval.Metric.PartsPct'), value: 'partsPct', kind: 'pct', color: '#f59e0b' },
    { label: t('CrmEval.Metric.Revenue', { currency: currencySymbol }), value: 'revenue', kind: 'eur', color: '#93c5fd' },
    { label: t('CrmEval.Metric.ClaimsCost', { currency: currencySymbol }), value: 'claimsCost', kind: 'eur', color: '#3b82f6' },
    { label: t('CrmEval.Metric.ClaimsCostPct'), value: 'costPct', kind: 'pct', color: '#3b82f6' },
  ]), [currencySymbol, t]);
  const TREND_DISPLAY_OPTIONS = useMemo(() => ([
    { label: t('CrmEval.Display.Counts'), value: 'count' },
    { label: currencySymbol, value: 'eur' },
    { label: t('CrmEval.Display.Percents'), value: 'pct' },
    { label: t('CrmEval.Display.CountsCurrency', { currency: currencySymbol }), value: 'count_eur' },
    { label: t('CrmEval.Display.CountsPct'), value: 'count_pct' },
    { label: `${currencySymbol} + %`, value: 'eur_pct' },
  ]), [currencySymbol, t]);
  const TREND_DISPLAY_KINDS = useMemo(() => ({
    count: ['count'],
    eur: ['eur'],
    pct: ['pct'],
    count_eur: ['count', 'eur'],
    count_pct: ['count', 'pct'],
    eur_pct: ['eur', 'pct'],
  }), []);
  const getMetricsForDisplayType = useCallback((displayType) => {
    const kinds = TREND_DISPLAY_KINDS[displayType] || [];
    return TREND_METRICS.filter((m) => kinds.includes(m.kind)).map((m) => ({ label: m.label, value: m.value }));
  }, [TREND_DISPLAY_KINDS, TREND_METRICS]);
  const ensureSeriesAllowedByDisplayType = useCallback((displayType, series) => {
    const allowed = new Set(getMetricsForDisplayType(displayType).map((o) => o.value));
    const filtered = (series || []).filter((v) => allowed.has(v));
    if (filtered.length > 0) return filtered;
    const defaults = getMetricsForDisplayType(displayType).map((o) => o.value);
    return defaults.length > 0 ? [defaults[0]] : [];
  }, [getMetricsForDisplayType]);
  const setTrendChartDisplayType = useCallback((id, displayType) => {
    setTrendCharts((prev) => prev.map((ch) => (ch.id === id
      ? { ...ch, displayType, series: [] }
      : ch)));
  }, []);
  const setTrendChartSeries = useCallback((id, series) => {
    setTrendCharts((prev) => prev.map((ch) => (ch.id === id
      ? { ...ch, series: ensureSeriesAllowedByDisplayType(ch.displayType, series) }
      : ch)));
  }, [ensureSeriesAllowedByDisplayType]);
  const setTrendChartChartType = useCallback((id, chartType) => {
    setTrendCharts((prev) => prev.map((ch) => (ch.id === id
      ? { ...ch, chartType: chartType === 'line' ? 'line' : 'bar' }
      : ch)));
  }, []);
  const TREND_METRIC_LABELS = useMemo(
    () => Object.fromEntries(TREND_METRICS.map((m) => [m.value, m.label])),
    [TREND_METRICS]
  );
  const buildTrendChartData = useCallback((chartConfig) => {
    const labels = trendFilteredRows.map((n) => n.data.departmentName);
    const displayType = chartConfig?.displayType || 'pct';
    const series = ensureSeriesAllowedByDisplayType(displayType, chartConfig?.series || []);
    const metricByValue = Object.fromEntries(TREND_METRICS.map((m) => [m.value, m]));
    const computeTrendLine = (values) => {
      const arr = (values || []).map((v) => Number(v || 0));
      const n = arr.length;
      if (n < 2) return arr;
      const xMean = (n - 1) / 2;
      const yMean = arr.reduce((a, b) => a + b, 0) / n;
      let num = 0;
      let den = 0;
      arr.forEach((y, i) => {
        const dx = i - xMean;
        num += dx * (y - yMean);
        den += dx * dx;
      });
      const slope = den === 0 ? 0 : num / den;
      const intercept = yMean - slope * xMean;
      return arr.map((_, i) => slope * i + intercept);
    };
    return {
      labels,
      datasets: series.flatMap((metricKey) => {
        const metric = metricByValue[metricKey];
        const color = metric?.color || '#64748b';
        const values = trendFilteredRows.map((n) => Number(n.data?.[metricKey] || 0));
        const yAxisID = ['count_pct', 'eur_pct'].includes(displayType) && metric?.kind === 'pct'
          ? 'y1'
          : (displayType === 'count_eur' && metric?.kind === 'eur' ? 'y1' : 'y');
        return [
          {
            label: metric?.label || metricKey,
            data: values,
            yAxisID,
            borderColor: color,
            backgroundColor: color,
            tension: 0.3,
          },
          {
            type: 'line',
            label: `Trend ${metric?.label || metricKey}`,
            data: computeTrendLine(values),
            yAxisID,
            borderColor: color,
            borderWidth: 1.5,
            borderDash: [6, 4],
            pointRadius: 0,
            spanGaps: true,
          },
        ];
      }),
    };
  }, [trendFilteredRows, ensureSeriesAllowedByDisplayType, TREND_METRICS]);

  const getTrendChartOptions = useCallback((displayType, printFontSize = null) => {
    const isPrint = !!printFontSize;
    const font = { size: printFontSize ?? 11, ...(isPrint && { color: '#000' }) };
    const axisColor = isPrint ? { color: '#000' } : {};
    const gridColor = isPrint ? { grid: { color: '#000' } } : {};
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: { legend: { position: 'bottom', labels: { font, ...(isPrint && { color: '#000' }) } } },
      scales: {
        x: {
          ticks: { autoSkip: true, maxRotation: 45, minRotation: 0, font, ...axisColor },
          ...gridColor,
        },
      },
    };
    if (displayType === 'pct') {
      base.scales.y = {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        ticks: { callback: (v) => `${v}%`, font, ...axisColor },
        title: { display: true, text: t('CrmEval.Axis.ShareOverTime'), font, ...axisColor },
        ...gridColor,
      };
      return base;
    }
    if (displayType === 'count' || displayType === 'eur') {
      base.scales.y = {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: { display: true, text: displayType === 'count' ? t('CrmEval.Axis.CountsPc') : currencySymbol, font, ...axisColor },
        ticks: { font, ...axisColor },
        ...gridColor,
      };
      return base;
    }
    base.scales.y = {
      type: 'linear',
      position: 'left',
      beginAtZero: true,
      title: { display: true, text: t('CrmEval.Axis.CountsPc'), font, ...axisColor },
      ticks: { font, ...axisColor },
      ...gridColor,
    };
    base.scales.y1 = {
      type: 'linear',
      position: 'right',
      beginAtZero: true,
      grid: { drawOnChartArea: false, ...(isPrint && { color: '#000' }) },
      title: { display: true, text: currencySymbol, font, ...axisColor },
      ticks: { font, ...axisColor },
      ...axisColor,
    };
    if (displayType === 'count_pct' || displayType === 'eur_pct') {
      base.scales.y1 = {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false, ...(isPrint && { color: '#000' }) },
        ticks: { callback: (v) => `${v}%`, font, ...axisColor },
        title: { display: true, text: '%', font, ...axisColor },
        ...axisColor,
      };
    }
    return base;
  }, []);
  const trendChartsDataAndOptions = useMemo(
    () => (trendCharts || []).map((ch) => ({
      data: buildTrendChartData(ch),
      options: getTrendChartOptions(ch.displayType, printChartFontSize),
    })),
    [trendCharts, buildTrendChartData, getTrendChartOptions, printChartFontSize]
  );
  const chartOptions = useMemo(() => {
    const isPrint = !!printChartFontSize;
    const font = { size: printChartFontSize ?? 11, ...(isPrint && { color: '#000' }) };
    return {
      maintainAspectRatio: false,
      plugins: { legend: { display: true, labels: { font, ...(isPrint && { color: '#000' }) } } },
      scales: {
        x: { grid: { display: false }, ticks: { font, ...(isPrint && { color: '#000' }) } },
        y: {
          beginAtZero: true,
          ticks: { font, ...(isPrint && { color: '#000' }) },
          ...(isPrint && { color: '#000', grid: { color: '#000' } }),
        },
      },
    };
  }, [printChartFontSize]);

  const trendDisplayOptions = TREND_DISPLAY_OPTIONS;
  const trendMetricLabels = TREND_METRIC_LABELS;
  const trendChartTypeOptions = useMemo(() => ([
    { label: t('CrmEval.ChartType.Bar'), value: 'bar' },
    { label: t('CrmEval.ChartType.Line'), value: 'line' },
  ]), []);

  const addTrendChart = useCallback(() => {
    setTrendCharts((prev) => {
      const curr = Array.isArray(prev) ? prev : [];
      if (curr.length >= TREND_MAX_CHARTS) return curr;
      return [...curr, {
        id: Date.now(),
        chartType: 'bar',
        displayType: 'count',
        series: [],
      }];
    });
  }, []);
  const removeTrendChart = useCallback((id) => {
    setTrendCharts((prev) => (prev.length <= 1 ? prev : prev.filter((x) => x.id !== id)));
  }, []);
  const startToolbar = (
    <div className="flex items-center gap-3">
      <Button
        icon="pi pi-refresh"
        label={loading ? t('CustomerEvaluationList.Action.Loading') : t('CustomerEvaluationList.Action.Refresh')}
        className="p-button-sm p-button-outlined p-2"
        onClick={loadData}
        disabled={loading}
      />
      <Button
        icon="pi pi-save"
        label={t('Common.SaveMask')}
        className="p-button-sm p-button-outlined p-2"
        onClick={saveMask}
      />
      <Button
        icon="pi pi-refresh"
        label={t('Common.MaskReset')}
        className="p-button-sm p-button-outlined p-button-danger p-2"
        onClick={resetMask}
      />
    </div>
  );

  const rightToolbar = (
    <div className="flex flex-wrap items-center justify-end gap-3 max-w-full">
      <MultiSelect
        value={effectiveFilters.trader_ids}
        options={traderOptions}
        onChange={(e) => {
          setTradersTouched(true);
          setCustomersTouched(false);
          setFilters((prev) => ({ ...prev, trader_ids: e.value || [], customer_ids: [] }));
        }}
        placeholder={t('CustomerEvaluationList.Filter.Trader')}
        className="w-56"
        disabled={!canSelectTrader}
        maxSelectedLabels={2}
        selectedItemsLabel={t('CrmEval.Selected.Traders')}
        showSelectAll
        selectAllLabel={t('CustomerEvaluationList.Filter.AllTraders')}
      />
      <MultiSelect
        value={effectiveFilters.customer_ids}
        options={customerOptions}
        onChange={(e) => {
          setCustomersTouched(true);
          setFilters((prev) => ({ ...prev, customer_ids: e.value || [] }));
        }}
        placeholder={t('CustomerEvaluationList.Filter.Customer')}
        className="w-56"
        maxSelectedLabels={2}
        selectedItemsLabel={t('CrmEval.Selected.Customers')}
        showSelectAll
        selectAllLabel={t('CustomerEvaluationList.Filter.AllCustomers')}
      />
      <MultiSelect
        value={selectedAckStates}
        options={ACK_STATE_OPTIONS}
        onChange={(e) => setSelectedAckStates(e.value || [])}
        placeholder={t('Common.Status')}
        className="w-56"
        showSelectAll
        selectAllLabel={t('Common.All')}
      />
      <Button
        icon="pi pi-print"
        label={t('CustomerEvaluationList.Action.Print')}
        className="p-button-sm p-button-outlined p-2"
        disabled={loading || !!loadError}
        onClick={() => {
          setPrintSelection(['all']);
          setPrintDialogVisible(true);
        }}
      />
    </div>
  );

  const fmtInt = (n) => new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 0 }).format(Number(n || 0));
  const fmtPct = (n) => formatPercent(n, { locale: 'sk', maximumFractionDigits: 2, empty: '0 %' });
  const fmtMoney = (n) => formatCurrency(n, currencySymbol, 'sk') || formatCurrency(0, currencySymbol, 'sk');
  const customerCellBody = (row) => (
    <div
      title={row?.customer || ''}
      style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {row?.customer || ''}
    </div>
  );
  const getRefForKey = useCallback((key) => {
    if (key === 'all') return refAllTablesTab2.current;
    if (key === 'claimedOrders') return refClaimedOrders.current;
    if (key === 'claimedPieces') return refClaimedPieces.current;
    if (key === 'repairCost') return refRepairCost.current;
    if (key === 'trendPeriod') return refTrendPeriod.current;
    return null;
  }, []);

  const printHasCharts = useCallback(() => evaluationPrintContentHasCharts({
    printSelection,
    allSectionKeys: CRM_EVALUATION_PRINT_KEYS,
    getRefForKey,
    allContainerRef: refAllTablesTab2,
  }), [printSelection, getRefForKey]);

  const buildPrintContentAsync = useCallback(() => buildEvaluationPrintContentAsync({
    printContainerRef,
    printSelection,
    allSectionKeys: CRM_EVALUATION_PRINT_KEYS,
    getRefForKey,
    logoSrc: crmPrintLogoSrc,
    hasCharts: printHasCharts(),
    onPrepareCharts: async () => {
      setIsPrintMode(true);
      await new Promise((r) => setTimeout(r, 200));
      setPrintChartFontSize(18);
    },
  }), [printSelection, getRefForKey, crmPrintLogoSrc, printHasCharts]);

  const handlePrint = useReactToPrint({
    content: () => (printContainerRef.current && printSelection.length > 0 ? printContainerRef.current : null),
    onBeforeGetContent: () => buildPrintContentAsync().then((ok) => {
      if (ok === false) throw new Error('PRINT_EMPTY');
    }),
    onAfterPrint: () => {
      setPrintChartFontSize(null);
      setIsPrintMode(false);
    },
    onPrintError: () => {
      setPrintChartFontSize(null);
      setIsPrintMode(false);
      toast?.current?.show({
        severity: 'warn',
        summary: t('Common.Print'),
        detail: t('CrmEval.Print.NoContent'),
        life: 4000,
      });
    },
    documentTitle: 'Vyhodnotenie_zakaznikov',
    pageStyle: getEvaluationPrintPageStyle('crm-evaluation-root'),
  });
  const shouldRenderSection = () => true;
  const shouldRenderTrendPart = () => true;
  const debugSectionClass = () => '';

  return (
    <div className="crm-evaluation-root flex flex-col h-full min-h-0 w-full min-w-0 overflow-hidden">
      <Toolbar start={startToolbar} end={rightToolbar} className="shadow-1" />
      {loading && (
        <div className="flex-1 flex items-center justify-center p-8 text-gray-500">
          <i className="pi pi-spin pi-spinner mr-2" style={{ fontSize: '1.5rem' }} />
          {t('Common.LoadingData')}
        </div>
      )}
      {loadError && !loading && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-red-600">
            <i className="pi pi-exclamation-triangle mb-2" style={{ fontSize: '1.5rem' }} />
            <p>{loadError}</p>
          </div>
        </div>
      )}
      {!loading && !loadError && (
      <div className="flex-1 min-h-0 w-full min-w-0 overflow-y-auto overflow-x-hidden p-4 space-y-6 pb-10">
        <TabView className="w-full min-w-0" activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} renderActiveOnly={false}>
          <TabPanel header={t('Menu.CRM.EvaluationCustomers')}>
            <div ref={refAllTablesTab2} className="flex flex-col w-full min-w-0 gap-4">
              <div ref={refClaimedOrders} className={`print-full print-table-section min-w-0 ${debugSectionClass('claimedOrders')} ${shouldRenderSection('claimedOrders') ? '' : 'hidden'}`}>
        <Card
          title={(
            <div className="flex justify-between items-center w-full">
              <span>{t('Menu.CRM.EvaluationCustomers')}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 no-print">{t('CrmEval.ComparePeriod')}</span>
                <InputSwitch
                  className="no-print"
                  checked={compareClaimedOrdersMode}
                  onChange={(e) => setCompareClaimedOrdersMode(e.value)}
                />
              </div>
            </div>
          )}
        >
          {!compareClaimedOrdersMode ? (
            <>
              <div className="flex items-center gap-3 mb-4 no-print">
                <span className="text-sm text-gray-700 font-medium">{t('Common.Period')}:</span>
                <Calendar
                  value={claimedOrdersLeftRange || null}
                  onChange={(e) => setClaimedOrdersLeftRange(e.value)}
                  selectionMode="range"
                  view="month"
                  dateFormat="mm/yy"
                  showIcon
                  className="w-[220px] border border-gray-400 rounded-md"
                />
              </div>

              <div className="overflow-x-auto max-w-full">
              <DataTable
                value={claimedOrdersLeftRowsAll}
                dataKey="_key"
                {...tableScrollProps}
                removableSort
                multiSortMeta={claimedOrdersLeftSortMeta}
                onSort={(e) => setClaimedOrdersLeftSortMeta(e.multiSortMeta)}
                sortMode="multiple"
                filterDisplay="row"
                filters={filtersClaimedOrdersLeft}
                onFilter={(e) => setFiltersClaimedOrdersLeft(e.filters)}
                emptyMessage={t('CustomerEvaluationList.Table.Empty')}
                className="w-full"
                tableStyle={{ minWidth: '700px' }}
              >
                <Column
                  field="customer"
                  header={t('Common.Customer')}
                  frozen
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={t('Common.TotalLabel')}
                  body={customerCellBody}
                  filterElement={(
                    <Dropdown
                      value={filtersClaimedOrdersLeft.customer?.value}
                      options={customerOptionsForClaimedOrders}
                      onChange={(e) => setFiltersClaimedOrdersLeft((prev) => ({ ...prev, customer: { value: e.value, matchMode: 'equals' } }))}
                      placeholder={t('Common.SelectCustomer')}
                      className="w-full h-[42px]"
                      showClear
                    />
                  )}
                />
                <Column
                  field="ordersTotal"
                  header={t('CrmEval.Metric.OrdersTotal')}
                  sortable
                  filter
                  showFilterMenu={false}
                  body={(r) => fmtInt(r.ordersTotal)}
                  footer={fmtInt(totalsClaimedOrdersLeft.ordersTotal)}
                  filterElement={(
                    <SearchFilterInput
                      value={filtersClaimedOrdersLeft.ordersTotal?.value}
                      onChange={(val) => setFiltersClaimedOrdersLeft((prev) => ({ ...prev, ordersTotal: { value: val, matchMode: 'contains' } }))}
                    />
                  )}
                />
                <Column
                  field="ordersCount"
                  header={t('CrmEval.Metric.OrdersCount')}
                  sortable
                  filter
                  showFilterMenu={false}
                  body={(r) => renderClaimDrilldownCell(r.ordersCount, r, claimedOrdersLeftRange)}
                  footer={fmtInt(totalsClaimedOrdersLeft.ordersCount)}
                  filterElement={(
                    <SearchFilterInput
                      value={filtersClaimedOrdersLeft.ordersCount?.value}
                      onChange={(val) => setFiltersClaimedOrdersLeft((prev) => ({ ...prev, ordersCount: { value: val, matchMode: 'contains' } }))}
                    />
                  )}
                />
                <Column
                  field="ordersPct"
                  header={t('CrmEval.Metric.OrdersPct')}
                  sortable
                  filter
                  showFilterMenu={false}
                  body={(r) => fmtPct(r.ordersPct)}
                  footer={fmtPct(totalsClaimedOrdersLeft.ordersPct)}
                  filterElement={(
                    <SearchFilterInput
                      value={filtersClaimedOrdersLeft.ordersPct?.value}
                      onChange={(val) => setFiltersClaimedOrdersLeft((prev) => ({ ...prev, ordersPct: { value: val, matchMode: 'contains' } }))}
                    />
                  )}
                />
              </DataTable>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6 mb-4 no-print">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                  <Calendar
                    value={claimedOrdersLeftRange || null}
                    onChange={(e) => setClaimedOrdersLeftRange(e.value)}
                    selectionMode="range"
                    view="month"
                    dateFormat="mm/yy"
                    showIcon
                    className="w-[220px] border border-gray-400 rounded-md"
                  />
                </div>
                <div className="flex items-center gap-2 no-print">
                  <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov')}</span>
                  <Calendar
                    value={claimedOrdersRightRange || null}
                    onChange={(e) => setClaimedOrdersRightRange(e.value)}
                    selectionMode="range"
                    view="month"
                    dateFormat="mm/yy"
                    readOnlyInput
                    showIcon
                    className="w-[220px] border border-gray-400 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 print-compare-grid">
                <div className="print-compare-cell min-w-0">
                  <div className="overflow-x-auto max-w-full">
                  <DataTable
                    value={claimedOrdersLeftRowsAll}
                    dataKey="_key"
                    {...tableScrollProps}
                    removableSort
                    multiSortMeta={claimedOrdersLeftSortMeta}
                    onSort={(e) => setClaimedOrdersLeftSortMeta(e.multiSortMeta)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filters={filtersClaimedOrdersLeft}
                    onFilter={(e) => setFiltersClaimedOrdersLeft(e.filters)}
                    emptyMessage={t('CustomerEvaluationList.Table.Empty')}
                    className="w-full"
                    tableStyle={{ minWidth: '700px' }}
                  >
                    <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                    <Column field="ordersTotal" header={t('CrmEval.Metric.OrdersTotal')} sortable body={(r) => fmtInt(r.ordersTotal)} footer={fmtInt(totalsClaimedOrdersLeft.ordersTotal)} />
                    <Column field="ordersCount" header={t('CrmEval.Metric.OrdersCount')} sortable body={(r) => renderClaimDrilldownCell(r.ordersCount, r, claimedOrdersLeftRange)} footer={fmtInt(totalsClaimedOrdersLeft.ordersCount)} />
                    <Column field="ordersPct" header={t('CrmEval.Metric.OrdersPct')} sortable body={(r) => fmtPct(r.ordersPct)} footer={fmtPct(totalsClaimedOrdersLeft.ordersPct)} />
                  </DataTable>
                  </div>
                </div>
                <div className="print-compare-cell min-w-0">
                  <div className="overflow-x-auto max-w-full">
                  <DataTable
                    value={claimedOrdersRightRowsAll}
                    dataKey="_key"
                    {...tableScrollProps}
                    removableSort
                    multiSortMeta={claimedOrdersRightSortMeta}
                    onSort={(e) => setClaimedOrdersRightSortMeta(e.multiSortMeta)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filters={filtersClaimedOrdersRight}
                    onFilter={(e) => setFiltersClaimedOrdersRight(e.filters)}
                    emptyMessage={t('CustomerEvaluationList.Table.Empty')}
                    className="w-full"
                    tableStyle={{ minWidth: '700px' }}
                  >
                    <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                    <Column field="ordersTotal" header={t('CrmEval.Metric.OrdersTotal')} sortable body={(r) => fmtInt(r.ordersTotal)} footer={fmtInt(totalsClaimedOrdersRight.ordersTotal)} />
                    <Column field="ordersCount" header={t('CrmEval.Metric.OrdersCount')} sortable body={(r) => renderClaimDrilldownCell(r.ordersCount, r, claimedOrdersRightRange)} footer={fmtInt(totalsClaimedOrdersRight.ordersCount)} />
                    <Column field="ordersPct" header={t('CrmEval.Metric.OrdersPct')} sortable body={(r) => fmtPct(r.ordersPct)} footer={fmtPct(totalsClaimedOrdersRight.ordersPct)} />
                  </DataTable>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={`mt-4 print-charts-section ${compareClaimedOrdersMode ? 'print-charts-compare' : ''}`}>
            <div className="flex items-center gap-2 w-full no-print">
              <span className="font-bold">{t('CrmEval.Charts')}</span>
              <InputSwitch checked={showClaimedOrdersCharts} onChange={(e) => setShowClaimedOrdersCharts(e.value)} />
            </div>
            {showClaimedOrdersCharts ? (
              <div className="mt-3 grid grid-cols-1 gap-4 print-chart-row">
                {!compareClaimedOrdersMode ? (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.OrdersPct'), range: fmtMonthRange(claimedOrdersLeftRange) })}>
                      <Chart
                        type="bar"
                        data={{
                          labels: claimedOrdersLeftRowsAll.map((r) => truncateLabel(r.customer)),
                          datasets: [{ label: t('CrmEval.Metric.OrdersPct'), data: claimedOrdersLeftRowsAll.map((r) => Number(r.ordersPct || 0)) }],
                        }}
                        options={chartOptions}
                      />
                    </Card>
                    <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.OrdersCount'), range: fmtMonthRange(claimedOrdersLeftRange) })}>
                      <Chart
                        type="bar"
                        data={{
                          labels: claimedOrdersLeftRowsAll.map((r) => truncateLabel(r.customer)),
                          datasets: [{ label: t('CrmEval.Metric.OrdersCount'), data: claimedOrdersLeftRowsAll.map((r) => Number(r.ordersCount || 0)) }],
                        }}
                        options={chartOptions}
                      />
                    </Card>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.OrdersPct'), range: fmtMonthRange(claimedOrdersLeftRange) })}>
                        <Chart
                          type="bar"
                          data={{ labels: claimedOrdersLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.OrdersPct'), data: claimedOrdersLeftRowsAll.map((r) => Number(r.ordersPct || 0)) }] }}
                          options={chartOptions}
                        />
                      </Card>
                      <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.OrdersPct'), range: fmtMonthRange(claimedOrdersRightRange) })}>
                        <Chart
                          type="bar"
                          data={{ labels: claimedOrdersRightRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.OrdersPct'), data: claimedOrdersRightRowsAll.map((r) => Number(r.ordersPct || 0)) }] }}
                          options={chartOptions}
                        />
                      </Card>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.OrdersCount'), range: fmtMonthRange(claimedOrdersLeftRange) })}>
                        <Chart
                          type="bar"
                          data={{ labels: claimedOrdersLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.OrdersCount'), data: claimedOrdersLeftRowsAll.map((r) => Number(r.ordersCount || 0)) }] }}
                          options={chartOptions}
                        />
                      </Card>
                      <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.OrdersCount'), range: fmtMonthRange(claimedOrdersRightRange) })}>
                        <Chart
                          type="bar"
                          data={{ labels: claimedOrdersRightRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.OrdersCount'), data: claimedOrdersRightRowsAll.map((r) => Number(r.ordersCount || 0)) }] }}
                          options={chartOptions}
                        />
                      </Card>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </Card>
              </div>

              <div ref={refClaimedPieces} className={`print-full print-table-section print-section-break min-w-0 ${debugSectionClass('claimedPieces')} ${shouldRenderSection('claimedPieces') ? '' : 'hidden'}`}>
        <Card
          title={(
            <div className="flex justify-between items-center w-full">
              <span>{t('CrmEval.Section.ClaimedPieces')}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 no-print">{t('CrmEval.ComparePeriod')}</span>
                <InputSwitch className="no-print" checked={compareClaimedPiecesMode} onChange={(e) => setCompareClaimedPiecesMode(e.value)} />
              </div>
            </div>
          )}
        >
          {!compareClaimedPiecesMode ? (
            <>
              <div className="flex items-center gap-3 mb-4 no-print">
                <span className="text-sm text-gray-700 font-medium">{t('Common.Period')}:</span>
                <Calendar value={claimedPiecesLeftRange || null} onChange={(e) => setClaimedPiecesLeftRange(e.value)} selectionMode="range" view="month" dateFormat="mm/yy" showIcon className="w-[220px] border border-gray-400 rounded-md" />
              </div>
              <div className="overflow-x-auto max-w-full">
              <DataTable value={claimedPiecesLeftRowsAll} dataKey="_key" {...tableScrollProps} removableSort multiSortMeta={claimedPiecesLeftSortMeta} onSort={(e) => setClaimedPiecesLeftSortMeta(e.multiSortMeta)} sortMode="multiple" filterDisplay="row" filters={filtersClaimedPiecesLeft} onFilter={(e) => setFiltersClaimedPiecesLeft(e.filters)} className="w-full" tableStyle={{ minWidth: '700px' }}>
                <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                <Column field="partsTotal" header={t('CrmEval.Metric.PartsTotal')} sortable body={(r) => fmtInt(r.partsTotal)} footer={fmtInt(totalsClaimedPiecesLeft.partsTotal)} />
                <Column field="partsCount" header={t('CrmEval.Metric.PartsCount')} sortable body={(r) => renderClaimDrilldownCell(r.partsCount, r, claimedPiecesLeftRange)} footer={fmtInt(totalsClaimedPiecesLeft.partsCount)} />
                <Column field="partsPct" header={t('CrmEval.Metric.PartsPct')} sortable body={(r) => fmtPct(r.partsPct)} footer={fmtPct(totalsClaimedPiecesLeft.partsPct)} />
              </DataTable>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6 mb-4 no-print">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                  <Calendar value={claimedPiecesLeftRange || null} onChange={(e) => setClaimedPiecesLeftRange(e.value)} selectionMode="range" view="month" dateFormat="mm/yy" showIcon className="w-[220px] border border-gray-400 rounded-md" />
                </div>
                <div className="flex items-center gap-2 no-print">
                  <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov')}</span>
                  <Calendar value={claimedPiecesRightRange || null} onChange={(e) => setClaimedPiecesRightRange(e.value)} selectionMode="range" view="month" dateFormat="mm/yy" readOnlyInput showIcon className="w-[220px] border border-gray-400 rounded-md" />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 print-compare-grid">
                <div className="print-compare-cell min-w-0">
                  <div className="overflow-x-auto max-w-full">
                  <DataTable value={claimedPiecesLeftRowsAll} dataKey="_key" {...tableScrollProps} removableSort multiSortMeta={claimedPiecesLeftSortMeta} onSort={(e) => setClaimedPiecesLeftSortMeta(e.multiSortMeta)} sortMode="multiple" filterDisplay="row" filters={filtersClaimedPiecesLeft} onFilter={(e) => setFiltersClaimedPiecesLeft(e.filters)} className="w-full" tableStyle={{ minWidth: '700px' }}>
                    <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                    <Column field="partsTotal" header={t('CrmEval.Metric.PartsTotal')} sortable body={(r) => fmtInt(r.partsTotal)} footer={fmtInt(totalsClaimedPiecesLeft.partsTotal)} />
                    <Column field="partsCount" header={t('CrmEval.Metric.PartsCount')} sortable body={(r) => renderClaimDrilldownCell(r.partsCount, r, claimedPiecesLeftRange)} footer={fmtInt(totalsClaimedPiecesLeft.partsCount)} />
                    <Column field="partsPct" header={t('CrmEval.Metric.PartsPct')} sortable body={(r) => fmtPct(r.partsPct)} footer={fmtPct(totalsClaimedPiecesLeft.partsPct)} />
                  </DataTable>
                  </div>
                </div>
                <div className="print-compare-cell min-w-0">
                  <div className="overflow-x-auto max-w-full">
                  <DataTable value={claimedPiecesRightRowsAll} dataKey="_key" {...tableScrollProps} removableSort multiSortMeta={claimedPiecesRightSortMeta} onSort={(e) => setClaimedPiecesRightSortMeta(e.multiSortMeta)} sortMode="multiple" filterDisplay="row" filters={filtersClaimedPiecesRight} onFilter={(e) => setFiltersClaimedPiecesRight(e.filters)} className="w-full" tableStyle={{ minWidth: '700px' }}>
                    <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                    <Column field="partsTotal" header={t('CrmEval.Metric.PartsTotal')} sortable body={(r) => fmtInt(r.partsTotal)} footer={fmtInt(totalsClaimedPiecesRight.partsTotal)} />
                    <Column field="partsCount" header={t('CrmEval.Metric.PartsCount')} sortable body={(r) => renderClaimDrilldownCell(r.partsCount, r, claimedPiecesRightRange)} footer={fmtInt(totalsClaimedPiecesRight.partsCount)} />
                    <Column field="partsPct" header={t('CrmEval.Metric.PartsPct')} sortable body={(r) => fmtPct(r.partsPct)} footer={fmtPct(totalsClaimedPiecesRight.partsPct)} />
                  </DataTable>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className={`mt-4 print-charts-section ${compareClaimedPiecesMode ? 'print-charts-compare' : ''}`}>
          <div className="no-print flex items-center gap-2">
            <span className="font-bold">{t('CrmEval.Charts')}</span>
            <InputSwitch checked={showClaimedPiecesCharts} onChange={(e) => setShowClaimedPiecesCharts(e.value)} />
          </div>
          {showClaimedPiecesCharts ? (
            <div className="mt-3 grid grid-cols-1 gap-4 print-chart-row">
              {!compareClaimedPiecesMode ? (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.PartsPct'), range: fmtMonthRange(claimedPiecesLeftRange) })}>
                    <Chart
                      type="bar"
                      data={{ labels: claimedPiecesLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.PartsPct'), data: claimedPiecesLeftRowsAll.map((r) => Number(r.partsPct || 0)) }] }}
                      options={chartOptions}
                    />
                  </Card>
                  <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.PartsCount'), range: fmtMonthRange(claimedPiecesLeftRange) })}>
                    <Chart
                      type="bar"
                      data={{ labels: claimedPiecesLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.PartsCount'), data: claimedPiecesLeftRowsAll.map((r) => Number(r.partsCount || 0)) }] }}
                      options={chartOptions}
                    />
                  </Card>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.PartsPct'), range: fmtMonthRange(claimedPiecesLeftRange) })}>
                      <Chart type="bar" data={{ labels: claimedPiecesLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.PartsPct'), data: claimedPiecesLeftRowsAll.map((r) => Number(r.partsPct || 0)) }] }} options={chartOptions} />
                    </Card>
                    <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.PartsPct'), range: fmtMonthRange(claimedPiecesRightRange) })}>
                      <Chart type="bar" data={{ labels: claimedPiecesRightRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.PartsPct'), data: claimedPiecesRightRowsAll.map((r) => Number(r.partsPct || 0)) }] }} options={chartOptions} />
                    </Card>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.PartsCount'), range: fmtMonthRange(claimedPiecesLeftRange) })}>
                      <Chart type="bar" data={{ labels: claimedPiecesLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.PartsCount'), data: claimedPiecesLeftRowsAll.map((r) => Number(r.partsCount || 0)) }] }} options={chartOptions} />
                    </Card>
                    <Card className="print-chart-item" title={t('CrmEval.TitleWithRange', { label: t('CrmEval.Metric.PartsCount'), range: fmtMonthRange(claimedPiecesRightRange) })}>
                      <Chart type="bar" data={{ labels: claimedPiecesRightRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.PartsCount'), data: claimedPiecesRightRowsAll.map((r) => Number(r.partsCount || 0)) }] }} options={chartOptions} />
                    </Card>
                  </div>
                </>
              )}
            </div>
          ) : null}
          </div>
        </Card>
              </div>

              <div ref={refRepairCost} className={`print-full print-table-section print-section-break min-w-0 ${debugSectionClass('repairCost')} ${shouldRenderSection('repairCost') ? '' : 'hidden'}`}>
        <Card
          title={(
            <div className="flex justify-between items-center w-full">
              <span>{t('CrmEval.Section.RepairCost')}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 no-print">{t('CrmEval.ComparePeriod')}</span>
                <InputSwitch className="no-print" checked={compareRepairCostMode} onChange={(e) => setCompareRepairCostMode(e.value)} />
              </div>
            </div>
          )}
        >
          {!compareRepairCostMode ? (
            <>
              <div className="flex items-center gap-3 mb-4 no-print">
                <span className="text-sm text-gray-700 font-medium">{t('Common.Period')}:</span>
                <Calendar value={repairCostLeftRange || null} onChange={(e) => setRepairCostLeftRange(e.value)} selectionMode="range" view="month" dateFormat="mm/yy" showIcon className="w-[220px] border border-gray-400 rounded-md" />
              </div>
              <div className="overflow-x-auto max-w-full">
              <DataTable value={repairCostLeftRowsAll} dataKey="_key" {...tableScrollProps} removableSort multiSortMeta={repairCostLeftSortMeta} onSort={(e) => setRepairCostLeftSortMeta(e.multiSortMeta)} sortMode="multiple" filterDisplay="row" filters={filtersRepairCostLeft} onFilter={(e) => setFiltersRepairCostLeft(e.filters)} className="w-full" tableStyle={{ minWidth: '700px' }}>
                <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                <Column field="revenue" header={t('CrmEval.Metric.Revenue', { currency: currencySymbol })} sortable body={(r) => fmtMoney(r.revenue)} footer={fmtMoney(totalsRepairCostLeft.revenue)} />
                <Column field="costEUR" header={t('CrmEval.Metric.ClaimsCost', { currency: currencySymbol })} sortable body={(r) => fmtMoney(r.costEUR)} footer={fmtMoney(totalsRepairCostLeft.costEUR)} />
                <Column field="costPct" header={t('CrmEval.Metric.ClaimsCostPct')} sortable body={(r) => fmtPct(r.costPct)} footer={fmtPct(totalsRepairCostLeft.costPct)} />
              </DataTable>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6 mb-4 no-print">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Lava_tabulka_mesiac')}</span>
                  <Calendar value={repairCostLeftRange || null} onChange={(e) => setRepairCostLeftRange(e.value)} selectionMode="range" view="month" dateFormat="mm/yy" showIcon className="w-[220px] border border-gray-400 rounded-md" />
                </div>
                <div className="flex items-center gap-2 no-print">
                  <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Prava_tabulka_od_do_mesiacov')}</span>
                  <Calendar value={repairCostRightRange || null} onChange={(e) => setRepairCostRightRange(e.value)} selectionMode="range" view="month" dateFormat="mm/yy" readOnlyInput showIcon className="w-[220px] border border-gray-400 rounded-md" />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 print-compare-grid">
                <div className="print-compare-cell min-w-0">
                  <div className="overflow-x-auto max-w-full">
                  <DataTable value={repairCostLeftRowsAll} dataKey="_key" {...tableScrollProps} removableSort multiSortMeta={repairCostLeftSortMeta} onSort={(e) => setRepairCostLeftSortMeta(e.multiSortMeta)} sortMode="multiple" filterDisplay="row" filters={filtersRepairCostLeft} onFilter={(e) => setFiltersRepairCostLeft(e.filters)} className="w-full" tableStyle={{ minWidth: '700px' }}>
                    <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                    <Column field="revenue" header={t('CrmEval.Metric.Revenue', { currency: currencySymbol })} sortable body={(r) => fmtMoney(r.revenue)} footer={fmtMoney(totalsRepairCostLeft.revenue)} />
                    <Column field="costEUR" header={t('CrmEval.Metric.ClaimsCost', { currency: currencySymbol })} sortable body={(r) => fmtMoney(r.costEUR)} footer={fmtMoney(totalsRepairCostLeft.costEUR)} />
                    <Column field="costPct" header={t('CrmEval.Metric.ClaimsCostPct')} sortable body={(r) => fmtPct(r.costPct)} footer={fmtPct(totalsRepairCostLeft.costPct)} />
                  </DataTable>
                  </div>
                </div>
                <div className="print-compare-cell min-w-0">
                  <div className="overflow-x-auto max-w-full">
                  <DataTable value={repairCostRightRowsAll} dataKey="_key" {...tableScrollProps} removableSort multiSortMeta={repairCostRightSortMeta} onSort={(e) => setRepairCostRightSortMeta(e.multiSortMeta)} sortMode="multiple" filterDisplay="row" filters={filtersRepairCostRight} onFilter={(e) => setFiltersRepairCostRight(e.filters)} className="w-full" tableStyle={{ minWidth: '700px' }}>
                    <Column field="customer" header={t('Common.Customer')} frozen sortable filter showFilterMenu={false} footer={t('Common.TotalLabel')} body={customerCellBody} />
                    <Column field="revenue" header={t('CrmEval.Metric.Revenue', { currency: currencySymbol })} sortable body={(r) => fmtMoney(r.revenue)} footer={fmtMoney(totalsRepairCostRight.revenue)} />
                    <Column field="costEUR" header={t('CrmEval.Metric.ClaimsCost', { currency: currencySymbol })} sortable body={(r) => fmtMoney(r.costEUR)} footer={fmtMoney(totalsRepairCostRight.costEUR)} />
                    <Column field="costPct" header={t('CrmEval.Metric.ClaimsCostPct')} sortable body={(r) => fmtPct(r.costPct)} footer={fmtPct(totalsRepairCostRight.costPct)} />
                  </DataTable>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className={`mt-4 print-charts-section ${compareRepairCostMode ? 'print-charts-compare' : ''}`}>
          <div className="no-print flex items-center gap-2">
            <span className="font-bold">{t('CrmEval.Charts')}</span>
            <InputSwitch checked={showRepairCostCharts} onChange={(e) => setShowRepairCostCharts(e.value)} />
          </div>
          {showRepairCostCharts ? (
            <div className="mt-3 grid grid-cols-1 gap-4 print-chart-row">
              {!compareRepairCostMode ? (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <Card className="print-chart-item" title={t('CrmEval.CostPctWithRange', { range: fmtMonthRange(repairCostLeftRange) })}>
                    <Chart type="bar" data={{ labels: repairCostLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.CostPct'), data: repairCostLeftRowsAll.map((r) => Number(r.costPct || 0)) }] }} options={chartOptions} />
                  </Card>
                  <Card className="print-chart-item" title={t('CrmEval.CostCurrencyWithRange', { currency: currencySymbol, range: fmtMonthRange(repairCostLeftRange) })}>
                    <Chart type="bar" data={{ labels: repairCostLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.CostWithCurrency', { currency: currencySymbol }), data: repairCostLeftRowsAll.map((r) => Number(r.costEUR || 0)) }] }} options={chartOptions} />
                  </Card>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card className="print-chart-item" title={t('CrmEval.ClaimsCostCurrencyWithRange', { currency: currencySymbol, range: fmtMonthRange(repairCostLeftRange) })}>
                      <Chart type="bar" data={{ labels: repairCostLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.ClaimsCost', { currency: currencySymbol }), data: repairCostLeftRowsAll.map((r) => Number(r.costEUR || 0)) }] }} options={chartOptions} />
                    </Card>
                    <Card className="print-chart-item" title={t('CrmEval.ClaimsCostCurrencyWithRange', { currency: currencySymbol, range: fmtMonthRange(repairCostRightRange) })}>
                      <Chart type="bar" data={{ labels: repairCostRightRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.ClaimsCost', { currency: currencySymbol }), data: repairCostRightRowsAll.map((r) => Number(r.costEUR || 0)) }] }} options={chartOptions} />
                    </Card>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card className="print-chart-item" title={t('CrmEval.ClaimsCostPctWithRange', { range: fmtMonthRange(repairCostLeftRange) })}>
                      <Chart type="bar" data={{ labels: repairCostLeftRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.ClaimsCostPct'), data: repairCostLeftRowsAll.map((r) => Number(r.costPct || 0)) }] }} options={chartOptions} />
                    </Card>
                    <Card className="print-chart-item" title={t('CrmEval.ClaimsCostPctWithRange', { range: fmtMonthRange(repairCostRightRange) })}>
                      <Chart type="bar" data={{ labels: repairCostRightRowsAll.map((r) => truncateLabel(r.customer)), datasets: [{ label: t('CrmEval.Metric.ClaimsCostPct'), data: repairCostRightRowsAll.map((r) => Number(r.costPct || 0)) }] }} options={chartOptions} />
                    </Card>
                  </div>
                </>
              )}
            </div>
          ) : null}
          </div>
        </Card>
              </div>

              <div ref={refTrendPeriod} className={`print-full print-table-section print-section-break min-w-0 overflow-x-hidden ${debugSectionClass('trendPeriod')} ${shouldRenderSection('trendPeriod') ? '' : 'hidden'}`}>
        <Card
          className="shadow-1 border border-[var(--surface-200)]"
          title={(
            <div className="flex justify-between items-center w-full">
              <span>{t('CrmEval.Section.Trend')}</span>
              <Button
                icon={Object.keys(expandedTrend).length > 0 ? 'pi pi-angle-down' : 'pi pi-angle-right'}
                className="p-button-text p-button-sm no-print"
                tooltip={Object.keys(expandedTrend).length > 0 ? t('Common.CollapseAll') : t('Common.ExpandAll')}
                tooltipOptions={{ position: 'left' }}
                onClick={() => {
                  if (Object.keys(expandedTrend).length > 0) {
                    setExpandedTrend({});
                  } else {
                    const next = {};
                    trendRows.forEach((r) => { next[r.key] = true; });
                    setExpandedTrend(next);
                  }
                }}
              />
            </div>
          )}
        >
          <div className="w-full min-w-0 overflow-x-hidden">
          <div className="flex flex-wrap items-center gap-4 mt-2 no-print">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 font-medium">{t('Ui.R67.Zobrazit')}</span>
              <Dropdown
                value={trendPeriodMode}
                options={[
                  { label: t('Common.Period.Month'), value: 'month' },
                  { label: t('Common.Period.Year'), value: 'year' },
                ]}
                onChange={(e) => {
                  setTrendPeriodMode(e.value);
                  setExpandedTrend({});
                }}
                className="w-[160px] h-[38px] items-center border border-gray-400 rounded-md"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 font-medium">{t('Common.Period')}:</span>
              <Calendar
                value={trendRangeUI}
                onChange={(e) => setTrendRangeUI(e.value)}
                selectionMode="range"
                view={trendPeriodMode === 'year' ? 'year' : 'month'}
                dateFormat={trendPeriodMode === 'year' ? 'yy' : 'mm/yy'}
                showIcon
                readOnlyInput
                hideOnRangeSelection={false}
                className="w-[220px] border border-gray-400 rounded-md"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 font-medium">Zobrazenie:</span>
              <Dropdown
                value={trendTableMode}
                options={[
                  { label: t('CrmEval.Display.CountsCurrency', { currency: currencySymbol }), value: 'abs' },
                  { label: t('CrmEval.Display.Percents'), value: 'pct' },
                ]}
                onChange={(e) => setTrendTableMode(e.value)}
                className="w-[180px] h-[38px] items-center border border-gray-400 rounded-md"
              />
            </div>
          </div>

          {shouldRenderTrendPart('trendTable') ? (
          <div className="mt-4 w-full min-w-0 overflow-x-auto max-w-full">
            <TreeTable
              value={trendFilteredRows}
              dataKey="key"
              expandedKeys={expandedTrend}
              onToggle={(e) => setExpandedTrend(e.value)}
              filters={filtersTrend}
              onFilter={(e) => setFiltersTrend(e.filters)}
              filterMode="lenient"
              multiSortMeta={trendSortMeta}
              onSort={(e) => setTrendSortMeta(e.multiSortMeta)}
              className="w-full min-w-0"
              tableStyle={{ tableLayout: 'auto', width: '100%' }}
              {...tableScrollProps}
              sortMode="multiple"
              removableSort
            >
              <Column
                field="departmentName"
                header={t('Common.Period')}
                expander
                sortable
                filter
                showFilterMenu={false}
                footer={t('Common.TotalLabel')}
                filterElement={(
                  <Dropdown
                    value={filtersTrend.departmentName?.value ?? null}
                    options={trendCustomerNameOptions}
                    onChange={(e) => setFiltersTrend((prev) => ({ ...prev, departmentName: { value: e.value, matchMode: 'equals' } }))}
                    placeholder={t('Common.Select')}
                    className="w-full h-[42px]"
                    showClear
                  />
                )}
              />
              <Column
                field="ordersTotal"
                header={t('CrmEval.Col.OrdersTotal')}
                body={(row) => fmtInt(row.data.ordersTotal)}
                sortable
                filter
                showFilterMenu={false}
                footer={fmtInt(totalsTrend.ordersTotal)}
                filterElement={<SearchFilterInput value={filtersTrend.ordersTotal?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, ordersTotal: { value: val, matchMode: 'contains' } }))} />}
              />
              {trendTableMode === 'abs' ? (
                <Column
                  field="ordersCount"
                  header={t('CrmEval.Col.OrdersCount')}
                  body={(row) => fmtInt(row.data.ordersCount)}
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={fmtInt(totalsTrend.ordersCount)}
                  filterElement={<SearchFilterInput value={filtersTrend.ordersCount?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, ordersCount: { value: val, matchMode: 'contains' } }))} />}
                />
              ) : (
                <Column
                  field="ordersPct"
                  header={t('CrmEval.Col.OrdersPct')}
                  body={(row) => fmtPct(row.data.ordersPct)}
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={fmtPct(totalsTrend.ordersPct)}
                  filterElement={<SearchFilterInput value={filtersTrend.ordersPct?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, ordersPct: { value: val, matchMode: 'contains' } }))} />}
                />
              )}
              <Column
                field="partsTotal"
                header={t('CrmEval.Col.PartsTotal')}
                body={(row) => fmtInt(row.data.partsTotal)}
                sortable
                filter
                showFilterMenu={false}
                footer={fmtInt(totalsTrend.partsTotal)}
                filterElement={<SearchFilterInput value={filtersTrend.partsTotal?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, partsTotal: { value: val, matchMode: 'contains' } }))} />}
              />
              {trendTableMode === 'abs' ? (
                <Column
                  field="partsCount"
                  header={t('CrmEval.Col.PartsCount')}
                  body={(row) => fmtInt(row.data.partsCount)}
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={fmtInt(totalsTrend.partsCount)}
                  filterElement={<SearchFilterInput value={filtersTrend.partsCount?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, partsCount: { value: val, matchMode: 'contains' } }))} />}
                />
              ) : (
                <Column
                  field="partsPct"
                  header="Rekl. kusy (%)"
                  body={(row) => fmtPct(row.data.partsPct)}
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={fmtPct(totalsTrend.partsPct)}
                  filterElement={<SearchFilterInput value={filtersTrend.partsPct?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, partsPct: { value: val, matchMode: 'contains' } }))} />}
                />
              )}
              <Column
                field="revenue"
                header={t('CrmEval.Col.Revenue', { currency: currencySymbol })}
                body={(row) => fmtMoney(row.data.revenue)}
                sortable
                filter
                showFilterMenu={false}
                footer={fmtMoney(totalsTrend.revenue)}
                filterElement={<SearchFilterInput value={filtersTrend.revenue?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, revenue: { value: val, matchMode: 'contains' } }))} />}
              />
              {trendTableMode === 'abs' ? (
                <Column
                  field="claimsCost"
                  header={t('CrmEval.Col.ClaimsCost', { currency: currencySymbol })}
                  body={(row) => fmtMoney(row.data.claimsCost)}
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={fmtMoney(totalsTrend.claimsCost)}
                  filterElement={<SearchFilterInput value={filtersTrend.claimsCost?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, claimsCost: { value: val, matchMode: 'contains' } }))} />}
                />
              ) : (
                <Column
                  field="costPct"
                  header={t('CrmEval.CostPct')}
                  body={(row) => fmtPct(row.data.costPct)}
                  sortable
                  filter
                  showFilterMenu={false}
                  footer={fmtPct(totalsTrend.costPct)}
                  filterElement={<SearchFilterInput value={filtersTrend.costPct?.value} onChange={(val) => setFiltersTrend((p) => ({ ...p, costPct: { value: val, matchMode: 'contains' } }))} />}
                />
              )}
            </TreeTable>
          </div>
          ) : null}

          {shouldRenderTrendPart('trendCharts') ? (
            <div className={`mt-4 print-charts-section ${showTrendCharts ? '' : ''}`}>
              <div className="flex flex-col gap-4 w-full min-w-0">
                {trendCharts.slice(0, TREND_MAX_CHARTS).map((chartCfg, idx) => (
                  <div
                    key={chartCfg.id}
                    className="border border-[var(--surface-200)] rounded-lg bg-white p-3 print-chart-item"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3 no-print">
                      <div className="font-semibold text-slate-800">
                        Graf {idx + 1}
                      </div>
                      {idx > 0 && (
                        <Button
                          icon="pi pi-trash"
                          className="p-button-text p-button-danger p-button-sm"
                          onClick={() => removeTrendChart(chartCfg.id)}
                          tooltip={t('CrmEval.RemoveChart')}
                          tooltipOptions={{ position: 'top' }}
                        />
                      )}
                    </div>

                    <div className="print-trend-chart-info mb-2 text-xs text-gray-700">
                      <span><strong>Zobrazenie:</strong> {trendDisplayOptions.find((o) => o.value === chartCfg.displayType)?.label ?? chartCfg.displayType}</span>
                      {' · '}
                      <span><strong>Typ:</strong> {trendChartTypeOptions.find((o) => o.value === (chartCfg.chartType ?? 'bar'))?.label ?? t('CrmEval.ChartType.Bar')}</span>
                      {' · '}
                      <span><strong>Metriky:</strong> {(chartCfg.series || []).map((v) => trendMetricLabels[v] ?? v).join(', ') || '—'}</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mb-3 no-print w-full min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-gray-700 font-medium">Zobrazenie:</span>
                        <Dropdown
                          value={chartCfg.displayType}
                          options={trendDisplayOptions}
                          onChange={(e) => setTrendChartDisplayType(chartCfg.id, e.value)}
                          className="w-full sm:w-[180px] h-[38px] items-center border border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-gray-700 font-medium">Typ grafu:</span>
                        <Dropdown
                          value={chartCfg.chartType ?? 'bar'}
                          options={trendChartTypeOptions}
                          onChange={(e) => setTrendChartChartType(chartCfg.id, e.value)}
                          className="w-full sm:w-[140px] h-[38px] items-center border border-gray-400 rounded-md"
                        />
                      </div>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-sm text-gray-700 font-medium">Metriky:</span>
                        <MultiSelect
                          value={chartCfg.series}
                          options={getMetricsForDisplayType(chartCfg.displayType)}
                          onChange={(e) => setTrendChartSeries(chartCfg.id, e.value)}
                          className="w-full lg:w-[420px] h-[38px] items-center border border-gray-400 rounded-md"
                          maxSelectedLabels={3}
                          selectedItemsLabel={t('CrmEval.Selected.Metrics')}
                          placeholder={t('CrmEval.SelectMetrics')}
                        />
                      </div>
                    </div>

                    <div className="h-[420px]">
                      <Chart
                        type={chartCfg.chartType ?? 'bar'}
                        style={{ width: '100%', height: '100%' }}
                        data={trendChartsDataAndOptions[idx]?.data}
                        options={trendChartsDataAndOptions[idx]?.options}
                      />
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between gap-3 pt-2 no-print">
                  <span className="text-sm text-gray-600">
                    Grafy ({Math.min(trendCharts.length, TREND_MAX_CHARTS)}/{TREND_MAX_CHARTS})
                  </span>
                  <Button
                    label={t('CrmEval.AddChart')}
                    icon="pi pi-plus"
                    className="p-button-sm p-3"
                    disabled={trendCharts.length >= TREND_MAX_CHARTS}
                    onClick={addTrendChart}
                  />
                </div>
              </div>
            </div>
          ) : null}
          </div>
        </Card>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
      )}
      <Dialog
        visible={printDialogVisible}
        header={t('Common.PrintSelectSections')}
        style={{ width: '34rem' }}
        modal
        onHide={() => setPrintDialogVisible(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button label={t('Common.Cancel')} icon="pi pi-times" className="p-button-text" onClick={() => setPrintDialogVisible(false)} />
            <Button
              label={t('Common.PrintAction')}
              icon="pi pi-print"
              disabled={printSelection.length === 0}
              onClick={() => {
                setPrintDialogVisible(false);
                setTimeout(() => handlePrint(), 350);
              }}
            />
          </div>
        )}
      >
        <div className="flex flex-col gap-3 pt-1">
          {printSections.map((item) => (
            <div key={item.key} className="flex items-center gap-2">
              <Checkbox
                inputId={`crm-print-${item.key}`}
                checked={printSelection.includes(item.key)}
                onChange={(e) => {
                  const checked = e.checked;
                  setPrintSelection((prev) => {
                    if (item.key === 'all') return checked ? ['all'] : [];
                    const base = prev.filter((x) => x !== 'all');
                    if (checked) return [...new Set([...base, item.key])];
                    return base.filter((x) => x !== item.key);
                  });
                }}
              />
              <label htmlFor={`crm-print-${item.key}`}>{item.label}</label>
            </div>
          ))}
        </div>
      </Dialog>
      <div
        ref={printContainerRef}
        className="print-body absolute -left-[9999px] w-px h-px overflow-hidden flex flex-col gap-4 print:static print:w-auto print:h-auto print:overflow-visible"
        aria-hidden="true"
      />
    </div>
  );
}
